import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import NoOffersMessage from 'src/components/NoOffersMessage'
import { useQueryClient } from '@tanstack/react-query'
import CircularProgress from '@material-ui/core/CircularProgress'
import FilterListIcon from '@material-ui/icons/FilterList'
import { breakpoint, breakpoints } from 'src/theme/grid'
import { MAX_RESERVATIONS_COUNT_FOR_QUEUE } from 'src/ducks/consts'
import { OFFER_TYPES } from 'src/features/offers/duck/consts'
import ContainerIcon from 'src/assets/icons/container.svg'
import ShipIcon from 'src/assets/icons/ship.svg'
import CompanyIcon from 'src/assets/icons/work-case.svg'
import RangeStepper from 'src/components/filters/components/RangeStepper'
import Select from 'src/components/filters/SelectBox'
import { Checkbox as FilterCheckbox } from 'src/components/filters/components/atoms'
import FavoriteOfferButton from 'src/features/stocks/components/OfferDetails/FavoriteOfferButton'
import { useOfferFavorites } from 'src/features/stocks/components/OfferDetails/offerFavorites'
import { createMapStyle } from './mapStyle'
import { OfferAgeFilter, OfferAgeFilterCount } from '../filters/OfferAgeFilter'
import {
  getOfferDateRangeFilters,
  getOfferDateRangeFromFilters
} from '../filters/offerAgeFilter'
import {
  EXCHANGE_MAP_TYPES,
  getDirectRoute,
  getOfferLocationMatchKeys,
  getRoundTripOfferKey,
  getRoundTripRoutesKey,
  getRoutesGeoJson,
  clusterOfferLocations,
  groupOfferLocations,
  hydrateMapOfferLocations,
  isPreplannedRoundTripOffer,
  isRoundTripSuggestionEnabled,
  normalizeMapLayers,
  normalizeMapOffer
} from './offerMapUtils'
import useSelectedOfferPage from './useSelectedOfferPage'
import useFilterPanelHeight from './useFilterPanelHeight'
import { EMPTY_MAP_PANEL_STATE } from './useExchangeMapPanelState'
import RoundTripRefreshNotice from './RoundTripRefreshNotice'
import { OFFER_MAP_FOCUS_EVENT } from './offerMapFocus'
import { createMapUpdateBatch } from './createMapUpdateBatch'
import {
  createRouteDistanceLabels,
  ROUTE_DISTANCE_LABEL_STYLES
} from './routeDistanceLabels'
import {
  fetchExchangeOfferRoute,
  MAP_LIST_PAGE_LIMIT
} from './useExchangeOffersMapQueries'

const DEFAULT_MAP_SOURCE_URL = 'https://tiles.openfreemap.org/planet'
const MAP_SOURCE_URL =
  process.env.GATSBY_MAP_TILES_URL ||
  process.env.GATSBY_PMTILES_URL ||
  DEFAULT_MAP_SOURCE_URL

const EMPTY_GEO_JSON = {
  type: 'FeatureCollection',
  features: []
}

const SELECTED_ROUTE_SOURCE_ID = 'selected-offer-route'
const SELECTED_ROUTE_ALTERNATES_LAYER_ID = 'selected-offer-route-alternates'
const SELECTED_ROUTE_LAYER_ID = 'selected-offer-route'
const ROUTE_REQUEST_CONCURRENCY = 4
const ROUTE_DISTANCE_BATCH_SIZE = 8
const ROUND_TRIP_PAGE_LIMIT = MAP_LIST_PAGE_LIMIT
const NOOP = () => {}
const EMPTY_LIVE_OFFER_TIMES = {}
const EMPTY_OFFERS = []
const handleOfferCardKeyDown = (event, onSelect) => {
  if (
    event.target === event.currentTarget &&
    (event.key === 'Enter' || event.key === ' ')
  ) {
    event.preventDefault()
    onSelect()
  }
}
const MARKER_PRIORITY_BAND = 1000000
const MAX_MARKER_OFFER_PRIORITY = MARKER_PRIORITY_BAND - 1
const MARKER_TOOLTIP_PRIORITY = MARKER_PRIORITY_BAND * 4
const MAP_UI_PRIORITY = MARKER_TOOLTIP_PRIORITY + MARKER_PRIORITY_BAND
// Keep the local favorites-filter implementation available for a possible
// return, but do not expose or apply it in the current map UI.
const FAVORITES_MAP_FILTER_ENABLED = false
const ATTRIBUTION_COLLAPSE_DELAY_MS = 5000
const CLUSTER_RADIUS = 42
const CLUSTER_EXPANSION_PADDING = 8
const MAX_CLUSTER_EXPANSION_ZOOM = 16
const ROUND_TRIP_EXPORT_COLOR = '#3559c7'
const ROUND_TRIP_ROUTE_COLOR = '#7358b6'
const getSelectedRouteColor = roundTrip =>
  roundTrip ? ROUND_TRIP_ROUTE_COLOR : '#147f98'
const ROUND_TRIP_ENDPOINT_KEYS = ['from', 'to']
const EMPTY_ROUND_TRIP_FILTERS = {
  exchangeTypes: [],
  maxDistanceKm: 50,
  daysAhead: 0,
  shipowner: [],
  container: [],
  createdAtFrom: null,
  createdAtTo: null
}

const getRoundTripFiltersForOffer = (
  mapOffer,
  filters,
  candidateExchangeTypes
) => ({
  ...filters,
  exchangeTypes: candidateExchangeTypes,
  shipowner: mapOffer.shipownerId ? [mapOffer.shipownerId] : [],
  container: mapOffer.containerId ? [mapOffer.containerId] : []
})

const getRoundTripLocationIndexKey = (exchangeType, endpointKey, locationKey) =>
  `${exchangeType || '*'}:${endpointKey}:${locationKey}`

const getIndexedRoundTripMatches = (
  index,
  exchangeType,
  endpointKey,
  location
) => {
  const matchesByKey = new Map()
  getOfferLocationMatchKeys(location).forEach(locationKey => {
    const matches =
      index.get(
        getRoundTripLocationIndexKey(exchangeType, endpointKey, locationKey)
      ) || []
    matches.forEach(match =>
      matchesByKey.set(`${match.exchangeType}:${match.id}`, match)
    )
  })
  return Array.from(matchesByKey.values())
}
const ENDPOINT_VISIBILITY = {
  ALL: 'all'
}

const FAVORITE_ENDPOINT_ROLES = {
  export: { from: 'origin', to: 'destination' },
  import: { from: 'origin', to: 'destination' },
  general: { from: 'origin', to: 'destination' },
  container: { from: 'origin', to: 'destination' },
  truck: { from: 'origin', to: 'destination' }
}

const OFFER_TYPE_TO_MAP_TYPE = {
  export: EXCHANGE_MAP_TYPES.EXPORT,
  import: EXCHANGE_MAP_TYPES.IMPORT,
  general: EXCHANGE_MAP_TYPES.GENERAL,
  container: EXCHANGE_MAP_TYPES.POST_IMPORT,
  truck: EXCHANGE_MAP_TYPES.FREE_CARRIERS
}

const ENDPOINT_LABEL_IDS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: {
    origin: 'offerDetails.loadingLocation',
    destination: 'offerDetails.returnLocation'
  },
  [EXCHANGE_MAP_TYPES.IMPORT]: {
    origin: 'offerDetails.pickupLocation',
    destination: 'offerDetails.unloadingLocation',
    return: 'offerDetails.returnLocation'
  },
  [EXCHANGE_MAP_TYPES.GENERAL]: {
    origin: 'offerDetails.loadingLocation',
    destination: 'offerDetails.unloadingLocation'
  },
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: {
    origin: 'offerDetails.availabilityLocation',
    destination: 'offerDetails.destinationLocation'
  },
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: {
    origin: 'offerDetails.availabilityLocation',
    destination: 'offerDetails.destinationLocation'
  }
}

const ENDPOINT_SHORT_LABEL_IDS = {
  origin: 'offers.mapFrom',
  destination: 'offers.mapTo',
  return: 'offers.mapReturn'
}

const ROUND_TRIP_TYPE_LABEL_IDS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: 'offers.mapRoundTripExport',
  [EXCHANGE_MAP_TYPES.GENERAL]: 'offers.mapRoundTripGeneral'
}

const ROUND_TRIP_ENDPOINT_ROLES = ['origin', 'return']

const ROUND_TRIP_ENDPOINT_LABEL_IDS = {
  origin: 'offerDetails.loadingLocation',
  return: 'offerDetails.returnLocation'
}

const capitalizeFirst = (value, locale) =>
  value
    ? `${value.charAt(0).toLocaleUpperCase(locale)}${value.slice(1)}`
    : value

const getRouteCacheKey = (from, to) =>
  `${from.join(':')}->${to.join(':')}:truck`

let pmtilesProtocol
let isPmtilesProtocolRegistered = false

const addOfferLayers = map => {
  map.addSource(SELECTED_ROUTE_SOURCE_ID, {
    type: 'geojson',
    data: EMPTY_GEO_JSON
  })
  map.addLayer({
    id: 'selected-offer-route-alternates-halo',
    type: 'line',
    source: SELECTED_ROUTE_SOURCE_ID,
    filter: [
      'all',
      ['==', ['get', 'featureKind'], 'route'],
      ['==', ['get', 'selected'], false]
    ],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': 'rgba(255, 255, 255, 0.84)',
      'line-width': 7
    }
  })
  map.addLayer({
    id: SELECTED_ROUTE_ALTERNATES_LAYER_ID,
    type: 'line',
    source: SELECTED_ROUTE_SOURCE_ID,
    filter: [
      'all',
      ['==', ['get', 'featureKind'], 'route'],
      ['==', ['get', 'selected'], false]
    ],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#7b858d',
      'line-width': 4,
      'line-opacity': 0.72
    }
  })
  map.addLayer({
    id: 'selected-offer-route-halo',
    type: 'line',
    source: SELECTED_ROUTE_SOURCE_ID,
    filter: [
      'all',
      ['==', ['get', 'featureKind'], 'route'],
      ['==', ['get', 'selected'], true]
    ],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': 'rgba(255, 255, 255, 0.95)',
      'line-width': 7
    }
  })
  map.addLayer({
    id: SELECTED_ROUTE_LAYER_ID,
    type: 'line',
    source: SELECTED_ROUTE_SOURCE_ID,
    filter: [
      'all',
      ['==', ['get', 'featureKind'], 'route'],
      ['==', ['get', 'selected'], true]
    ],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': [
        'case',
        ['get', 'roundTrip'],
        getSelectedRouteColor(true),
        getSelectedRouteColor(false)
      ],
      'line-width': ['case', ['get', 'roundTrip'], 5, 4]
    }
  })
}

const collapseAttributionControl = map => {
  const attributionControl = map
    .getContainer()
    .querySelector('.maplibregl-ctrl-attrib')

  if (!attributionControl) return

  attributionControl.removeAttribute('open')
  attributionControl.classList.remove('maplibregl-compact-show')
}

const createMarkerElement = ({
  group,
  active,
  focused,
  dimmed,
  roundTripCandidate,
  roundTripDestination,
  roundTripExchangeType,
  hasFavorite,
  roleLabel
}) => {
  const element = document.createElement('button')
  const pin = document.createElement('span')
  const count = document.createElement('span')
  const tooltip = document.createElement('span')
  const offersCount = Number(group.offersCount) || 0

  element.type = 'button'
  element.className = [
    'exchange-offers-marker',
    `exchange-offers-marker--${group.role}`,
    group.isCluster ? 'exchange-offers-marker--cluster' : '',
    active ? 'exchange-offers-marker--active' : '',
    focused ? 'exchange-offers-marker--focused' : '',
    roundTripCandidate ? 'exchange-offers-marker--round-trip-candidate' : '',
    roundTripExchangeType
      ? `exchange-offers-marker--round-trip-${roundTripExchangeType}`
      : '',
    dimmed ? 'exchange-offers-marker--dimmed' : ''
  ]
    .filter(Boolean)
    .join(' ')
  element.setAttribute(
    'aria-label',
    `${roleLabel}: ${group.mapLocation.name}, ${offersCount}`
  )
  const offerCountPriority = Math.min(offersCount, MAX_MARKER_OFFER_PRIORITY)
  let markerPriority = MARKER_PRIORITY_BAND
  if (dimmed) markerPriority = 0
  if (focused) markerPriority = MARKER_PRIORITY_BAND * 2
  if (roundTripDestination) markerPriority = MARKER_PRIORITY_BAND * 2
  if (active) markerPriority = MARKER_PRIORITY_BAND * 3
  const defaultMarkerPriority = markerPriority + offerCountPriority
  const updateMarkerPriority = () => {
    const tooltipIsVisible =
      element.matches(':hover') || document.activeElement === element
    element.style.zIndex = String(
      tooltipIsVisible ? MARKER_TOOLTIP_PRIORITY : defaultMarkerPriority
    )
  }

  updateMarkerPriority()
  element.addEventListener('mouseenter', updateMarkerPriority)
  element.addEventListener('mouseleave', updateMarkerPriority)
  element.addEventListener('focus', updateMarkerPriority)
  element.addEventListener('blur', () => {
    window.requestAnimationFrame(updateMarkerPriority)
  })

  pin.className = 'exchange-offers-marker__pin'
  count.className = 'exchange-offers-marker__count'
  tooltip.className = 'exchange-offers-marker__tooltip'
  tooltip.setAttribute('aria-hidden', 'true')
  count.textContent = offersCount
  tooltip.textContent = group.mapLocation.name
  pin.appendChild(count)
  element.appendChild(pin)
  if (hasFavorite) {
    const favoriteBadge = document.createElement('span')
    favoriteBadge.className = 'exchange-offers-marker__favorite'
    favoriteBadge.setAttribute('aria-hidden', 'true')
    favoriteBadge.textContent = '★'
    element.appendChild(favoriteBadge)
  }
  element.appendChild(tooltip)

  return element
}

const getLocationName = endpoint =>
  endpoint && endpoint.location && endpoint.location.name
    ? endpoint.location.name
    : '—'

const MapOfferRoute = ({ from, to, roundTrip = false }) => {
  const fromName = getLocationName(from)
  const toName = getLocationName(to)
  const preserveFromName =
    fromName.length <= 12 && fromName.length <= toName.length
  const preserveToName = toName.length <= 12 && toName.length < fromName.length

  return (
    <OfferRoute>
      <OfferEndpoint preserveName={preserveFromName}>
        <LocationName>{fromName}</LocationName>
        {from.dateTime && <OfferRouteDate>{from.dateTime}</OfferRouteDate>}
      </OfferEndpoint>
      {roundTrip ? (
        <RoundTripArrow>→</RoundTripArrow>
      ) : (
        <RouteArrow>→</RouteArrow>
      )}
      <OfferEndpoint preserveName={preserveToName}>
        <LocationName>{toName}</LocationName>
        {to.dateTime && <OfferRouteDate>{to.dateTime}</OfferRouteDate>}
      </OfferEndpoint>
    </OfferRoute>
  )
}

const getCoordinatesKey = mapLocation =>
  mapLocation.coordinates.map(coordinate => coordinate.toFixed(5)).join(':')

const getMarkerOffset = (group, locationGroups) => {
  const coordinateKey = getCoordinatesKey(group.mapLocation)
  const collocatedGroups = locationGroups
    .filter(
      otherGroup => getCoordinatesKey(otherGroup.mapLocation) === coordinateKey
    )
    .slice()
    .sort((firstGroup, secondGroup) =>
      firstGroup.id.localeCompare(secondGroup.id)
    )

  if (collocatedGroups.length < 2) return [0, 0]

  const groupIndex = collocatedGroups.findIndex(
    collocatedGroup => collocatedGroup.id === group.id
  )
  const angle =
    -Math.PI / 2 + (Math.PI * 2 * groupIndex) / collocatedGroups.length
  const radius = Math.min(18, 9 + collocatedGroups.length)

  return [
    Math.round(Math.cos(angle) * radius),
    Math.round(Math.sin(angle) * radius)
  ]
}

const getClusterExpansionZoom = (map, locationGroups) => {
  const currentZoom = map.getZoom()
  const expansionClusterRadius = CLUSTER_RADIUS + CLUSTER_EXPANSION_PADDING

  for (
    let candidateZoom = Math.min(currentZoom + 0.5, MAX_CLUSTER_EXPANSION_ZOOM);
    candidateZoom < MAX_CLUSTER_EXPANSION_ZOOM;
    candidateZoom = Math.min(candidateZoom + 0.25, MAX_CLUSTER_EXPANSION_ZOOM)
  ) {
    const scale = 2 ** (candidateZoom - currentZoom)
    const expandedGroups = clusterOfferLocations(
      locationGroups,
      coordinates => {
        const point = map.project(coordinates)
        return { x: point.x * scale, y: point.y * scale }
      },
      expansionClusterRadius
    )

    if (expandedGroups.length > 1) return candidateZoom
  }

  return MAX_CLUSTER_EXPANSION_ZOOM
}

const ExchangeOffersMap = ({
  activeDrawerOfferId,
  autoSelectBestRoundTrip = false,
  className,
  exchangeType,
  initialRoundTripOffer,
  isDrawerOpen,
  loading,
  mapLayers = [],
  mapOffersCount = 0,
  offersError,
  offersListError = false,
  offersLoading = false,
  onClusterSelect = NOOP,
  onPageChange,
  offers = [],
  onLocationSelect = NOOP,
  onOfferSelect,
  onRoundTripOffersLoad = NOOP,
  onRoundTripFiltersChange = NOOP,
  onRoundTripOfferSelect = NOOP,
  onSelectionClear = NOOP,
  onPanelStateChange = NOOP,
  panelState = EMPTY_MAP_PANEL_STATE,
  pagination,
  roundTripMapOffers = EMPTY_OFFERS,
  roundTripMatches: availableRoundTripMatches = EMPTY_OFFERS,
  roundTripCandidateTypes = [],
  roundTripContainerOptions = [],
  roundTripOffersError = false,
  roundTripFailedExchangeTypes = EMPTY_OFFERS,
  roundTripRefreshStatus,
  roundTripFilters = EMPTY_ROUND_TRIP_FILTERS,
  roundTripMapLayers = [],
  roundTripLiveOfferTimes = EMPTY_LIVE_OFFER_TIMES,
  roundTripOffersLoading = false,
  roundTripOnly = false,
  roundTripShipownerOptions = [],
  selectedOfferId: drawerSelectedOfferId,
  selectedOfferType: drawerSelectedOfferType
}) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const normalizedInitialRoundTripOffer = useMemo(
    () =>
      initialRoundTripOffer
        ? normalizeMapOffer(initialRoundTripOffer, exchangeType)
        : null,
    [exchangeType, initialRoundTripOffer]
  )
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const maplibreRef = useRef(null)
  const markersRef = useRef([])
  const routeDistanceLabelsRef = useRef(null)
  const offersPanelRef = useRef(null)
  const offersPanelListRef = useRef(null)
  const roundTripFiltersSectionRef = useRef(null)
  const endpointVisibilityControlRef = useRef(null)
  const favoritesControlRef = useRef(null)
  const layerKeysRef = useRef([])
  const markerTooltipReleaseTimeoutRef = useRef(null)
  const roundTripDistanceTimeoutRef = useRef(null)
  const roundTripModeRef = useRef(false)
  const initialRoundTripOfferKeyRef = useRef(null)
  const autoSelectedRoundTripSourceRef = useRef(null)
  const fittedLocationSignatureRef = useRef(null)
  const focusedGroupIdRef = useRef(null)
  const activeCluster =
    panelState.group && panelState.group.isCluster ? panelState.group : null
  const isClusterListVisible = panelState.showClusterList
  const isOffersPanelCollapsed = panelState.collapsed
  const [isLegendExpanded, setIsLegendExpanded] = useState(true)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [endpointVisibility, setEndpointVisibility] = useState(
    ENDPOINT_VISIBILITY.ALL
  )
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [mapError, setMapError] = useState(false)
  const [mapViewRevision, setMapViewRevision] = useState(0)
  const [selectedOfferId, setSelectedOfferId] = useState(() =>
    normalizedInitialRoundTripOffer ? normalizedInitialRoundTripOffer.id : null
  )
  const [focusedDrawerMapOffer, setFocusedDrawerMapOffer] = useState(
    normalizedInitialRoundTripOffer
  )
  const [roundTripSourceOfferId, setRoundTripSourceOfferId] = useState(() =>
    normalizedInitialRoundTripOffer ? normalizedInitialRoundTripOffer.id : null
  )
  const [isRoundTripFiltersExpanded, setIsRoundTripFiltersExpanded] = useState(
    false
  )
  const [roundTripDistanceDraft, setRoundTripDistanceDraft] = useState(
    roundTripFilters.maxDistanceKm
  )
  const [roundTripPage, setRoundTripPage] = useState(1)
  const [selectedRoundTripOfferKey, setSelectedRoundTripOfferKey] = useState(
    null
  )
  const [selectedRoundTripGroupId, setSelectedRoundTripGroupId] = useState(null)
  const [selectedOfferRoutes, setSelectedOfferRoutes] = useState([])
  const [selectedOfferRouteIndex, setSelectedOfferRouteIndex] = useState(0)
  const selectedOfferRoutesRef = useRef(selectedOfferRoutes)
  selectedOfferRoutesRef.current = selectedOfferRoutes
  const [routeDistances, setRouteDistances] = useState({})
  const routeDistancesRef = useRef(routeDistances)
  routeDistancesRef.current = routeDistances
  const endpointVisibilityStateRef = useRef(endpointVisibility)
  endpointVisibilityStateRef.current = endpointVisibility
  const favoritesFilterActiveRef = useRef(showFavoritesOnly)
  favoritesFilterActiveRef.current = showFavoritesOnly
  const { favorites, isFavorite, toggleFavorite } = useOfferFavorites()
  const favoriteLocationCounts = useMemo(() => {
    const locationCounts = new Map()
    const addFavoriteLocation = (role, location) => {
      if (!location || location.id == null) return

      const key = `${role}:${String(location.id)}`
      locationCounts.set(key, (locationCounts.get(key) || 0) + 1)
    }

    favorites.forEach(favorite => {
      const endpointRoles = FAVORITE_ENDPOINT_ROLES[favorite.offerType]
      if (
        !endpointRoles ||
        OFFER_TYPE_TO_MAP_TYPE[favorite.offerType] !== exchangeType
      ) {
        return
      }

      addFavoriteLocation(endpointRoles.from, favorite.fromLocation)
      addFavoriteLocation(endpointRoles.to, favorite.toLocation)
    })
    return locationCounts
  }, [exchangeType, favorites])
  const favoriteLocationKeys = useMemo(
    () => new Set(favoriteLocationCounts.keys()),
    [favoriteLocationCounts]
  )
  const hasMapFavorites = favoriteLocationCounts.size > 0
  const getCachedRouteDetails = useCallback(
    (from, to, signal, profile) =>
      fetchExchangeOfferRoute(queryClient, from, to, signal, profile),
    [queryClient]
  )
  const handleSelectedOfferRouteClick = useCallback(event => {
    const feature = event && event.features && event.features[0]
    const routeIndex = Number(
      feature && feature.properties && feature.properties.routeIndex
    )
    const route = selectedOfferRoutesRef.current[routeIndex]
    if (!Number.isInteger(routeIndex) || !route) return

    event.preventDefault()
    setSelectedOfferRouteIndex(routeIndex)
  }, [])

  const locationGroups = useMemo(() => normalizeMapLayers(mapLayers), [
    mapLayers
  ])
  const layerKeys = useMemo(
    () => Array.from(new Set(locationGroups.map(group => group.role))),
    [locationGroups]
  )
  layerKeysRef.current = layerKeys
  const locationsById = useMemo(() => {
    const locations = new Map()
    locationGroups.forEach(group => {
      group.locationIds.forEach(locationId => {
        locations.set(String(locationId), group.mapLocation)
      })
    })
    return locations
  }, [locationGroups])
  const mapOffers = useMemo(
    () =>
      offers
        .map(offer => normalizeMapOffer(offer, exchangeType))
        .filter(Boolean)
        .map(mapOffer => hydrateMapOfferLocations(mapOffer, locationsById)),
    [exchangeType, locationsById, offers]
  )
  const visibleMapOfferId = mapOffers.some(
    mapOffer =>
      selectedOfferId != null && String(mapOffer.id) === String(selectedOfferId)
  )
    ? selectedOfferId
    : null
  const activeLocationGroup =
    panelState.group && !activeCluster
      ? locationGroups.find(group => group.id === panelState.group.id) ||
        panelState.group
      : null
  const activeGroup = activeCluster || activeLocationGroup
  const selectedGroupId = activeGroup && activeGroup.id
  const activeLocationIdSet = useMemo(
    () =>
      activeGroup
        ? new Set(activeGroup.locationIds.map(locationId => String(locationId)))
        : new Set(),
    [activeGroup]
  )
  const visibleLocationGroups = useMemo(() => {
    const endpointGroups =
      endpointVisibility === ENDPOINT_VISIBILITY.ALL
        ? locationGroups
        : locationGroups.filter(
            group =>
              group.role === endpointVisibility ||
              group.locationIds.some(locationId =>
                activeLocationIdSet.has(String(locationId))
              )
          )
    if (!FAVORITES_MAP_FILTER_ENABLED || !showFavoritesOnly) {
      return endpointGroups
    }

    return endpointGroups.reduce((favoriteGroups, group) => {
      const favoriteOffersCount = group.locationIds.reduce(
        (count, locationId) =>
          count +
          (favoriteLocationCounts.get(`${group.role}:${String(locationId)}`) ||
            0),
        0
      )
      if (favoriteOffersCount === 0) return favoriteGroups

      return [...favoriteGroups, { ...group, offersCount: favoriteOffersCount }]
    }, [])
  }, [
    activeLocationIdSet,
    locationGroups,
    endpointVisibility,
    favoriteLocationCounts,
    showFavoritesOnly
  ])
  const locationSignature = useMemo(
    () =>
      locationGroups
        .map(group => `${group.id}:${group.mapLocation.coordinates.join(':')}`)
        .sort()
        .join('|'),
    [locationGroups]
  )
  const endpointLabelIds =
    ENDPOINT_LABEL_IDS[exchangeType] ||
    ENDPOINT_LABEL_IDS[EXCHANGE_MAP_TYPES.EXPORT]
  const endpointLabels = useMemo(
    () =>
      layerKeys.reduce(
        (labels, layerKey) => ({
          ...labels,
          [layerKey]: capitalizeFirst(
            intl.formatMessage({
              id:
                endpointLabelIds[layerKey] ||
                ENDPOINT_SHORT_LABEL_IDS[layerKey] ||
                'offers.mapDestinationPlace'
            }),
            intl.locale
          )
        }),
        {}
      ),
    [endpointLabelIds, intl, layerKeys]
  )
  const endpointVisibilityControlLabel = intl.formatMessage({
    id: 'offers.mapSwitchView'
  })
  const favoritesControlLabel = intl.formatMessage({
    id: 'offers.showFavorites'
  })
  const selectedMapOffer =
    focusedDrawerMapOffer ||
    mapOffers.find(mapOffer => mapOffer.id === selectedOfferId)
  const roundTripSuggestionsEnabled = isRoundTripSuggestionEnabled(exchangeType)
  const canShowRoundTripSuggestions = Boolean(
    roundTripSuggestionsEnabled &&
      selectedMapOffer &&
      !isPreplannedRoundTripOffer(selectedMapOffer)
  )
  const isRoundTripMode = Boolean(
    canShowRoundTripSuggestions &&
      (roundTripOnly || roundTripSourceOfferId === selectedMapOffer.id)
  )
  const {
    minimumHeight: preservedPanelHeight,
    preserveHeight: preservePanelHeight,
    resetHeight: resetPanelHeight
  } = useFilterPanelHeight(
    offersPanelRef,
    isRoundTripMode
      ? `${exchangeType}:return:${roundTripSourceOfferId}`
      : `${exchangeType}:${selectedGroupId}:${(pagination &&
          pagination.currentPage) ||
          1}`
  )
  roundTripModeRef.current = isRoundTripMode
  useEffect(() => {
    const mapOffer = normalizedInitialRoundTripOffer
    if (!mapOffer) return

    const destinationCoordinates = mapOffer.to.mapLocation
      ? mapOffer.to.mapLocation.coordinates.join(':')
      : 'no-coordinates'
    const sourceLocation = mapOffer.from.location || {}
    const sourceIdentity = [
      sourceLocation.id,
      sourceLocation.countryCode,
      sourceLocation.name,
      mapOffer.shipownerId,
      mapOffer.shipownerName,
      mapOffer.containerId,
      mapOffer.containerName
    ]
      .map(value => String(value || ''))
      .join(':')
    const requestKey = `${mapOffer.id}:${mapOffer.to.rawDate ||
      mapOffer.to.date}:${destinationCoordinates}:${sourceIdentity}`
    if (initialRoundTripOfferKeyRef.current === requestKey) return
    initialRoundTripOfferKeyRef.current = requestKey

    const initialFilters = roundTripOnly
      ? roundTripFilters
      : getRoundTripFiltersForOffer(
          mapOffer,
          roundTripFilters,
          roundTripCandidateTypes
        )
    onPanelStateChange(EMPTY_MAP_PANEL_STATE)
    setFocusedDrawerMapOffer(mapOffer)
    setSelectedOfferId(mapOffer.id)
    setRoundTripSourceOfferId(mapOffer.id)
    setSelectedRoundTripOfferKey(null)
    setSelectedRoundTripGroupId(null)
    setIsRoundTripFiltersExpanded(false)
    onRoundTripFiltersChange(initialFilters)
    onRoundTripOffersLoad(mapOffer, initialFilters)
  }, [
    exchangeType,
    normalizedInitialRoundTripOffer,
    onPanelStateChange,
    onRoundTripFiltersChange,
    onRoundTripOffersLoad,
    roundTripCandidateTypes,
    roundTripFilters
  ])
  const roundTripMatches = isRoundTripMode
    ? availableRoundTripMatches
    : EMPTY_OFFERS
  const roundTripLastPage = Math.max(
    1,
    Math.ceil(roundTripMatches.length / ROUND_TRIP_PAGE_LIMIT)
  )
  const activeRoundTripPage = Math.min(roundTripPage, roundTripLastPage)
  const displayedRoundTripMatches = useMemo(() => {
    const start = (activeRoundTripPage - 1) * ROUND_TRIP_PAGE_LIMIT
    return roundTripMatches.slice(start, start + ROUND_TRIP_PAGE_LIMIT)
  }, [activeRoundTripPage, roundTripMatches])
  const visibleRoundTripOfferKey = displayedRoundTripMatches.some(
    match => `${match.exchangeType}:${match.id}` === selectedRoundTripOfferKey
  )
    ? selectedRoundTripOfferKey
    : null
  const selectedRouteOffer = isRoundTripMode
    ? displayedRoundTripMatches.find(
        match => getRoundTripOfferKey(match) === visibleRoundTripOfferKey
      )
    : selectedMapOffer
  const selectedOfferRoutesKey = getRoundTripRoutesKey(
    selectedRouteOffer ? [selectedRouteOffer] : []
  )
  const selectedRouteCacheKey = useMemo(() => {
    const [request] = JSON.parse(selectedOfferRoutesKey)
    return request ? getRouteCacheKey(...request.endpoints) : null
  }, [selectedOfferRoutesKey])

  useSelectedOfferPage({
    isDrawerOpen,
    selectedOfferKey: selectedRoundTripOfferKey,
    matches: roundTripMatches,
    pageSize: ROUND_TRIP_PAGE_LIMIT,
    setPage: setRoundTripPage
  })
  const roundTripMatchesByLocation = useMemo(() => {
    const index = new Map()
    const appendMatch = (indexKey, match) => {
      if (!index.has(indexKey)) index.set(indexKey, [])
      index.get(indexKey).push(match)
    }

    roundTripMatches.forEach(match => {
      ROUND_TRIP_ENDPOINT_KEYS.forEach(endpointKey => {
        const endpoint = match[endpointKey]
        getOfferLocationMatchKeys(endpoint && endpoint.location).forEach(
          locationKey => {
            appendMatch(
              getRoundTripLocationIndexKey(
                match.exchangeType,
                endpointKey,
                locationKey
              ),
              match
            )
            appendMatch(
              getRoundTripLocationIndexKey(null, endpointKey, locationKey),
              match
            )
          }
        )
      })
    })

    return index
  }, [roundTripMatches])

  useEffect(() => {
    setRoundTripPage(1)
  }, [roundTripFilters, roundTripSourceOfferId])

  useEffect(() => {
    if (roundTripPage > roundTripLastPage) {
      setRoundTripPage(roundTripLastPage)
    }
  }, [roundTripLastPage, roundTripPage])

  const roundTripLayerGroups = useMemo(
    () =>
      roundTripMapLayers.reduce(
        (allGroups, candidateLayers) =>
          allGroups.concat(
            normalizeMapLayers(candidateLayers.layers).map(group => {
              const endpointKey = group.role === 'origin' ? 'from' : 'to'
              const markerRole = endpointKey === 'from' ? 'origin' : 'return'
              const matchingOffers = getIndexedRoundTripMatches(
                roundTripMatchesByLocation,
                candidateLayers.exchangeType,
                endpointKey,
                group.mapLocation
              )

              return {
                ...group,
                id: `round-trip:${candidateLayers.exchangeType}:${markerRole}:${group.id}`,
                role: markerRole,
                clusterKey: `round-trip:${markerRole}`,
                roundTripCandidate: true,
                offers: matchingOffers,
                offersCount: matchingOffers.length,
                roundTripInRange: matchingOffers.length > 0
              }
            })
          ),
        []
      ),
    [roundTripMapLayers, roundTripMatchesByLocation]
  )
  const roundTripLocationGroups = useMemo(() => {
    if (roundTripLayerGroups.length > 0) return roundTripLayerGroups

    return groupOfferLocations(roundTripMapOffers).map(group => {
      const endpointKey = group.role === 'from' ? 'from' : 'to'
      const markerRole = endpointKey === 'from' ? 'origin' : 'return'
      const matchingOffers = getIndexedRoundTripMatches(
        roundTripMatchesByLocation,
        null,
        endpointKey,
        group.mapLocation
      )

      return {
        ...group,
        id: `round-trip:${markerRole}:${group.id}`,
        role: markerRole,
        clusterKey: `round-trip:${markerRole}`,
        roundTripCandidate: true,
        offers: matchingOffers,
        offersCount: matchingOffers.length,
        roundTripInRange: matchingOffers.length > 0
      }
    })
  }, [roundTripLayerGroups, roundTripMapOffers, roundTripMatchesByLocation])
  const displayedPagination = isRoundTripMode
    ? {
        currentPage: activeRoundTripPage,
        from:
          displayedRoundTripMatches.length > 0
            ? (activeRoundTripPage - 1) * ROUND_TRIP_PAGE_LIMIT + 1
            : 0,
        lastPage: roundTripLastPage,
        to: Math.min(
          activeRoundTripPage * ROUND_TRIP_PAGE_LIMIT,
          roundTripMatches.length
        ),
        total: roundTripMatches.length
      }
    : pagination || {}
  const currentPage = Number(displayedPagination.currentPage) || 1
  const lastPage = Number(displayedPagination.lastPage) || 1
  const showPageNavigation = isRoundTripMode
    ? roundTripLastPage > 1
    : Boolean(onPageChange && lastPage > 1)
  const roundTripEndpointLabels = useMemo(
    () =>
      ROUND_TRIP_ENDPOINT_ROLES.reduce(
        (labels, role) => ({
          ...labels,
          [role]: capitalizeFirst(
            intl.formatMessage({ id: ROUND_TRIP_ENDPOINT_LABEL_IDS[role] }),
            intl.locale
          )
        }),
        {}
      ),
    [intl]
  )
  const renderedLocationGroups = isRoundTripMode
    ? roundTripLocationGroups
    : visibleLocationGroups
  const markerGroups = useMemo(() => {
    const map = mapRef.current
    if (
      !map ||
      !isMapLoaded ||
      map.getZoom() >= MAX_CLUSTER_EXPANSION_ZOOM - 0.05
    ) {
      return renderedLocationGroups
    }

    return clusterOfferLocations(
      renderedLocationGroups,
      coordinates => map.project(coordinates),
      CLUSTER_RADIUS
    )
  }, [isMapLoaded, mapViewRevision, renderedLocationGroups])
  const offersWithVisibleDistances = useMemo(() => {
    // Opening details prioritizes the chosen offer; already loaded distances
    // stay on the list, while unrelated requests wait until it is closed.
    if (isDrawerOpen) {
      const selected = selectedRouteOffer || selectedMapOffer
      return selected ? [selected] : EMPTY_OFFERS
    }
    return isRoundTripMode
      ? [selectedMapOffer, ...displayedRoundTripMatches].filter(Boolean)
      : mapOffers
  }, [
    displayedRoundTripMatches,
    isDrawerOpen,
    isRoundTripMode,
    mapOffers,
    selectedMapOffer,
    selectedRouteOffer
  ])
  const routeDistanceRequestsKey = useMemo(() => {
    const routes = new Map()
    offersWithVisibleDistances.forEach(mapOffer => {
      const endpoints = getDirectRoute(mapOffer)
      if (endpoints.length === 2) {
        routes.set(getRouteCacheKey(endpoints[0], endpoints[1]), endpoints)
      }
    })
    return JSON.stringify(
      Array.from(routes.entries()).sort(([first], [second]) =>
        first.localeCompare(second)
      )
    )
  }, [offersWithVisibleDistances])
  const focusedLocationIds = useMemo(() => {
    const locationIds = activeGroup
      ? activeGroup.locationIds.map(locationId => String(locationId))
      : []
    if (selectedMapOffer) {
      return Array.from(
        new Set(
          locationIds.concat(
            [selectedMapOffer.from.location, selectedMapOffer.to.location]
              .filter(Boolean)
              .map(location => String(location.id))
          )
        )
      )
    }
    return locationIds
  }, [activeGroup, selectedMapOffer])
  const clearMapSelection = useCallback(() => {
    if (roundTripDistanceTimeoutRef.current) {
      window.clearTimeout(roundTripDistanceTimeoutRef.current)
      roundTripDistanceTimeoutRef.current = null
    }
    onPanelStateChange(EMPTY_MAP_PANEL_STATE)
    setSelectedOfferId(null)
    setFocusedDrawerMapOffer(null)
    setRoundTripSourceOfferId(null)
    setIsRoundTripFiltersExpanded(false)
    setSelectedRoundTripOfferKey(null)
    setSelectedRoundTripGroupId(null)
    setSelectedOfferRoutes([])
    setSelectedOfferRouteIndex(0)
    resetPanelHeight()
  }, [onPanelStateChange, resetPanelHeight])

  useEffect(() => {
    const handleOfferMapFocus = event => {
      const offer = event && event.detail
      const selectedOfferExchangeType =
        offer && OFFER_TYPE_TO_MAP_TYPE[offer.offerType]
      if (
        !selectedOfferExchangeType ||
        selectedOfferExchangeType !== exchangeType
      ) {
        return
      }

      const mapOffer = hydrateMapOfferLocations(
        normalizeMapOffer(offer, selectedOfferExchangeType),
        locationsById
      )
      if (!mapOffer) return

      setRoundTripSourceOfferId(null)
      setSelectedRoundTripOfferKey(null)
      setSelectedRoundTripGroupId(null)
      // The route effect resets variants only when the offer or endpoints change.
      setFocusedDrawerMapOffer(mapOffer)
      setSelectedOfferId(mapOffer.id)
    }

    window.addEventListener(OFFER_MAP_FOCUS_EVENT, handleOfferMapFocus)
    return () =>
      window.removeEventListener(OFFER_MAP_FOCUS_EVENT, handleOfferMapFocus)
  }, [exchangeType, locationsById])

  useEffect(() => {
    // Endpoint identity is stable across drawer metadata, live updates and
    // selection styling. Those renders must not abort and restart the queue.
    const routes = JSON.parse(routeDistanceRequestsKey).filter(
      ([cacheKey]) => !Number.isFinite(routeDistancesRef.current[cacheKey])
    )
    if (routes.length === 0) return undefined

    const controller = new AbortController()
    let isCancelled = false
    let nextRouteIndex = 0
    const distanceUpdates = createMapUpdateBatch(entries => {
      const distanceBatch = {}
      entries.forEach(([cacheKey, distanceKm]) => {
        distanceBatch[cacheKey] = distanceKm
      })
      routeDistancesRef.current = {
        ...routeDistancesRef.current,
        ...distanceBatch
      }
      setRouteDistances(currentDistances => ({
        ...currentDistances,
        ...distanceBatch
      }))
    }, ROUTE_DISTANCE_BATCH_SIZE)

    const loadNextDistance = () => {
      if (isCancelled || nextRouteIndex >= routes.length) {
        return Promise.resolve()
      }

      const [cacheKey, endpoints] = routes[nextRouteIndex]
      nextRouteIndex += 1

      return getCachedRouteDetails(
        endpoints[0],
        endpoints[1],
        controller.signal,
        cacheKey === selectedRouteCacheKey ? 'alternatives' : 'summary'
      )
        .then(route => {
          if (!isCancelled) distanceUpdates.add([cacheKey, route.distanceKm])
        })
        .catch(() => {
          if (!isCancelled) distanceUpdates.add([cacheKey, null])
        })
        .then(loadNextDistance)
    }

    const workerCount = Math.min(ROUTE_REQUEST_CONCURRENCY, routes.length)
    Promise.all(Array.from({ length: workerCount }, loadNextDistance)).then(
      distanceUpdates.flush
    )

    return () => {
      isCancelled = true
      distanceUpdates.cancel()
      controller.abort()
    }
  }, [getCachedRouteDetails, routeDistanceRequestsKey, selectedRouteCacheKey])

  const handleSelectionClear = useCallback(() => {
    clearMapSelection()
    onSelectionClear()
  }, [clearMapSelection, onSelectionClear])
  const suppressMarkerTooltips = useCallback(releaseDelay => {
    const map = mapRef.current
    if (!map) return

    const container = map.getContainer()
    container.classList.add('exchange-offers-map--tooltips-suppressed')
    if (markerTooltipReleaseTimeoutRef.current) {
      window.clearTimeout(markerTooltipReleaseTimeoutRef.current)
      markerTooltipReleaseTimeoutRef.current = null
    }
    if (Number.isFinite(releaseDelay)) {
      markerTooltipReleaseTimeoutRef.current = window.setTimeout(() => {
        container.classList.remove('exchange-offers-map--tooltips-suppressed')
        markerTooltipReleaseTimeoutRef.current = null
      }, releaseDelay)
    }
  }, [])
  const cycleEndpointVisibility = useCallback(() => {
    setEndpointVisibility(currentVisibility => {
      const visibilityOptions = [
        ENDPOINT_VISIBILITY.ALL,
        ...layerKeysRef.current
      ]
      const currentIndex = visibilityOptions.indexOf(currentVisibility)
      return visibilityOptions[(currentIndex + 1) % visibilityOptions.length]
    })
  }, [])
  const toggleFavoritesFilter = useCallback(() => {
    clearMapSelection()
    setShowFavoritesOnly(isActive => !isActive)
    onSelectionClear()
  }, [clearMapSelection, onSelectionClear])

  useEffect(() => {
    if (
      endpointVisibility !== ENDPOINT_VISIBILITY.ALL &&
      !layerKeys.includes(endpointVisibility)
    ) {
      setEndpointVisibility(ENDPOINT_VISIBILITY.ALL)
    }
  }, [endpointVisibility, layerKeys])

  useEffect(() => {
    const button = endpointVisibilityControlRef.current
    if (!button) return

    button.dataset.visibility = endpointVisibility
    button.title = endpointVisibilityControlLabel
    button.setAttribute('aria-label', endpointVisibilityControlLabel)
  }, [endpointVisibility, endpointVisibilityControlLabel, isMapLoaded])

  useEffect(() => {
    const button = favoritesControlRef.current
    if (!button) return

    button.parentElement.style.display =
      FAVORITES_MAP_FILTER_ENABLED && hasMapFavorites ? '' : 'none'
    button.dataset.active = showFavoritesOnly ? 'true' : 'false'
    button.title = favoritesControlLabel
    button.setAttribute('aria-label', favoritesControlLabel)
    button.setAttribute('aria-pressed', String(showFavoritesOnly))
    if (!hasMapFavorites && showFavoritesOnly) {
      setShowFavoritesOnly(false)
    }
  }, [favoritesControlLabel, hasMapFavorites, showFavoritesOnly, isMapLoaded])

  useEffect(() => {
    let isMounted = true
    let mapHasLoaded = false
    let attributionCollapseTimeout

    const initializeMap = async () => {
      try {
        setMapError(false)
        setIsMapLoaded(false)
        const [
          maplibreModule,
          pmtilesModule,
          maplibreWorkerModule
        ] = await Promise.all([
          import('maplibre-gl/dist/maplibre-gl-csp'),
          import('pmtiles'),
          import(
            '!!file-loader?name=maplibre-gl-csp-worker-[contenthash].[ext]!maplibre-gl/dist/maplibre-gl-csp-worker.js'
          )
        ])
        if (!isMounted || !mapContainerRef.current) return

        const maplibregl = maplibreModule.default || maplibreModule
        maplibregl.setWorkerUrl(
          maplibreWorkerModule.default || maplibreWorkerModule
        )
        if (!isPmtilesProtocolRegistered) {
          pmtilesProtocol = new pmtilesModule.Protocol()
          maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile)
          isPmtilesProtocolRegistered = true
        }

        maplibreRef.current = maplibregl
        fittedLocationSignatureRef.current = null
        const map = new maplibregl.Map({
          container: mapContainerRef.current,
          style: createMapStyle(MAP_SOURCE_URL, intl.locale),
          center: [19.1451, 51.9194],
          zoom: 5.6,
          minZoom: 4,
          maxZoom: 18,
          attributionControl: { compact: true }
        })
        mapRef.current = map
        map.addControl(
          new maplibregl.NavigationControl({ showCompass: false }),
          'bottom-right'
        )
        map.addControl(new maplibregl.FullscreenControl(), 'bottom-right')
        let visibilityControlContainer
        let visibilityControlButton
        let favoritesControlContainer
        let favoritesControlButton
        const handleVisibilityControlClick = event => {
          event.stopPropagation()
          cycleEndpointVisibility()
        }
        map.addControl(
          {
            onAdd: () => {
              visibilityControlContainer = document.createElement('div')
              visibilityControlButton = document.createElement('button')
              const visibilityMarker = document.createElement('span')

              visibilityControlContainer.className =
                'maplibregl-ctrl maplibregl-ctrl-group exchange-offers-visibility-control'
              visibilityControlButton.type = 'button'
              visibilityControlButton.title = endpointVisibilityControlLabel
              visibilityControlButton.dataset.visibility =
                endpointVisibilityStateRef.current
              visibilityControlButton.setAttribute(
                'aria-label',
                endpointVisibilityControlLabel
              )
              visibilityMarker.className =
                'exchange-offers-visibility-control__marker'
              visibilityControlButton.appendChild(visibilityMarker)
              visibilityControlButton.addEventListener(
                'click',
                handleVisibilityControlClick
              )
              visibilityControlContainer.appendChild(visibilityControlButton)
              endpointVisibilityControlRef.current = visibilityControlButton

              return visibilityControlContainer
            },
            onRemove: () => {
              if (visibilityControlButton) {
                visibilityControlButton.removeEventListener(
                  'click',
                  handleVisibilityControlClick
                )
              }
              if (visibilityControlContainer) {
                visibilityControlContainer.remove()
              }
              if (
                endpointVisibilityControlRef.current === visibilityControlButton
              ) {
                endpointVisibilityControlRef.current = null
              }
            }
          },
          'bottom-right'
        )
        const handleFavoritesControlClick = event => {
          event.preventDefault()
          event.stopPropagation()
          if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
            event.nativeEvent.stopImmediatePropagation()
          } else if (event.stopImmediatePropagation) {
            event.stopImmediatePropagation()
          }
          toggleFavoritesFilter()
        }
        if (FAVORITES_MAP_FILTER_ENABLED) {
          map.addControl(
            {
              onAdd: () => {
                favoritesControlContainer = document.createElement('div')
                favoritesControlButton = document.createElement('button')
                const favoritesStar = document.createElement('span')

                favoritesControlContainer.className =
                  'maplibregl-ctrl maplibregl-ctrl-group exchange-offers-favorites-control'
                favoritesControlContainer.style.display = 'none'
                favoritesControlButton.type = 'button'
                favoritesControlButton.title = favoritesControlLabel
                favoritesControlButton.dataset.active = String(
                  favoritesFilterActiveRef.current
                )
                favoritesControlButton.setAttribute(
                  'aria-label',
                  favoritesControlLabel
                )
                favoritesControlButton.setAttribute(
                  'aria-pressed',
                  String(favoritesFilterActiveRef.current)
                )
                favoritesStar.className =
                  'exchange-offers-favorites-control__star'
                favoritesStar.textContent = '★'
                favoritesControlButton.appendChild(favoritesStar)
                favoritesControlButton.addEventListener(
                  'click',
                  handleFavoritesControlClick
                )
                favoritesControlContainer.appendChild(favoritesControlButton)
                favoritesControlRef.current = favoritesControlButton

                return favoritesControlContainer
              },
              onRemove: () => {
                if (favoritesControlButton) {
                  favoritesControlButton.removeEventListener(
                    'click',
                    handleFavoritesControlClick
                  )
                }
                if (favoritesControlContainer) {
                  favoritesControlContainer.remove()
                }
                if (favoritesControlRef.current === favoritesControlButton) {
                  favoritesControlRef.current = null
                }
              }
            },
            'bottom-right'
          )
        }
        map.on('click', event => {
          suppressMarkerTooltips(300)
          const selectedRouteLayers = [
            SELECTED_ROUTE_ALTERNATES_LAYER_ID,
            SELECTED_ROUTE_LAYER_ID
          ].filter(layerId => map.getLayer(layerId))
          if (
            selectedRouteLayers.length > 0 &&
            map.queryRenderedFeatures(event.point, {
              layers: selectedRouteLayers
            }).length > 0
          ) {
            return
          }
          if (roundTripModeRef.current) return
          handleSelectionClear()
        })
        map.on('movestart', () => suppressMarkerTooltips())
        map.on('moveend', () => {
          suppressMarkerTooltips(180)
          if (isMounted) setMapViewRevision(revision => revision + 1)
        })
        map.on('error', event => {
          if (!isMounted || mapHasLoaded || (event && event.tile)) return
          setMapError(true)
        })
        map.on('load', () => {
          if (!isMounted) return
          try {
            addOfferLayers(map)
            map.on(
              'click',
              SELECTED_ROUTE_ALTERNATES_LAYER_ID,
              handleSelectedOfferRouteClick
            )
            map.on('mouseenter', SELECTED_ROUTE_ALTERNATES_LAYER_ID, () => {
              map.getCanvas().style.cursor = 'pointer'
            })
            map.on('mouseleave', SELECTED_ROUTE_ALTERNATES_LAYER_ID, () => {
              map.getCanvas().style.cursor = ''
            })
            mapHasLoaded = true
            setMapError(false)
            setIsMapLoaded(true)
            attributionCollapseTimeout = window.setTimeout(
              () => collapseAttributionControl(map),
              ATTRIBUTION_COLLAPSE_DELAY_MS
            )
          } catch (error) {
            setMapError(true)
          }
        })
      } catch (error) {
        if (isMounted) setMapError(true)
      }
    }

    initializeMap()

    return () => {
      isMounted = false
      if (attributionCollapseTimeout) {
        window.clearTimeout(attributionCollapseTimeout)
      }
      if (markerTooltipReleaseTimeoutRef.current) {
        window.clearTimeout(markerTooltipReleaseTimeoutRef.current)
        markerTooltipReleaseTimeoutRef.current = null
      }
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
      if (routeDistanceLabelsRef.current) {
        routeDistanceLabelsRef.current.remove()
        routeDistanceLabelsRef.current = null
      }
      if (mapRef.current) mapRef.current.remove()
      mapRef.current = null
    }
  }, [
    cycleEndpointVisibility,
    endpointVisibilityControlLabel,
    favoritesControlLabel,
    handleSelectionClear,
    handleSelectedOfferRouteClick,
    intl.locale,
    suppressMarkerTooltips,
    toggleFavoritesFilter
  ])

  useEffect(() => {
    const panel = offersPanelListRef.current
    if (!panel || isRoundTripMode || (offersLoading && offers.length === 0)) {
      return
    }

    panel.scrollTop = panelState.scrollTop
  }, [
    isRoundTripMode,
    isOffersPanelCollapsed,
    offersLoading,
    offers,
    panelState
  ])

  useEffect(() => {
    setRoundTripDistanceDraft(roundTripFilters.maxDistanceKm)
  }, [roundTripFilters.maxDistanceKm])

  useEffect(() => {
    if (roundTripOnly || initialRoundTripOffer || isRoundTripMode) return

    if (roundTripDistanceTimeoutRef.current) {
      window.clearTimeout(roundTripDistanceTimeoutRef.current)
      roundTripDistanceTimeoutRef.current = null
    }
    setIsRoundTripFiltersExpanded(false)
  }, [isRoundTripMode])

  useEffect(
    () => () => {
      if (roundTripDistanceTimeoutRef.current) {
        window.clearTimeout(roundTripDistanceTimeoutRef.current)
      }
    },
    []
  )

  useEffect(() => {
    if (isRoundTripMode) return

    if (isDrawerOpen) {
      if (drawerSelectedOfferId != null) {
        setSelectedOfferId(drawerSelectedOfferId)
        setRoundTripSourceOfferId(currentOfferId =>
          currentOfferId === drawerSelectedOfferId ? currentOfferId : null
        )
      } else {
        setSelectedOfferId(null)
        setFocusedDrawerMapOffer(null)
        setRoundTripSourceOfferId(null)
      }
      return
    }

    setSelectedOfferId(null)
    setFocusedDrawerMapOffer(null)
    setRoundTripSourceOfferId(null)
    setSelectedRoundTripOfferKey(null)
  }, [
    drawerSelectedOfferId,
    initialRoundTripOffer,
    isDrawerOpen,
    isRoundTripMode,
    roundTripOnly
  ])

  useEffect(() => {
    if (!roundTripOnly || isDrawerOpen) return

    setSelectedRoundTripOfferKey(null)
  }, [isDrawerOpen, roundTripOnly])

  useEffect(() => {
    if (!isRoundTripMode || !isDrawerOpen) return

    const activeOfferKey =
      drawerSelectedOfferType && activeDrawerOfferId != null
        ? `${drawerSelectedOfferType}:${activeDrawerOfferId}`
        : null
    setSelectedRoundTripOfferKey(
      drawerSelectedOfferType === exchangeType ? null : activeOfferKey
    )
  }, [
    activeDrawerOfferId,
    drawerSelectedOfferType,
    exchangeType,
    isDrawerOpen,
    isRoundTripMode,
    roundTripOnly
  ])

  useEffect(() => {
    const map = mapRef.current
    const maplibregl = maplibreRef.current
    if (!map || !maplibregl || !isMapLoaded) return undefined

    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = markerGroups.map(group => {
      const markerLocationGroups = group.locationGroups || [group]
      const roundTripDestination = markerLocationGroups.some(
        locationGroup => locationGroup.roundTripInRange
      )
      const focused = isRoundTripMode
        ? group.id === selectedRoundTripGroupId
        : Boolean(
            focusedLocationIds.length > 0 &&
              group.locationIds.some(locationId =>
                focusedLocationIds.includes(String(locationId))
              )
          )
      const element = createMarkerElement({
        group,
        active: isRoundTripMode
          ? group.id === selectedRoundTripGroupId
          : group.id === selectedGroupId,
        focused,
        dimmed: isRoundTripMode
          ? !roundTripDestination
          : focusedLocationIds.length > 0 && !focused,
        roundTripCandidate: isRoundTripMode,
        roundTripDestination,
        roundTripExchangeType: null,
        hasFavorite:
          !isRoundTripMode &&
          group.locationIds.some(locationId =>
            favoriteLocationKeys.has(`${group.role}:${String(locationId)}`)
          ),
        roleLabel:
          (isRoundTripMode
            ? roundTripEndpointLabels[group.role]
            : endpointLabels[group.role]) || group.role
      })
      element.addEventListener('click', event => {
        event.stopPropagation()
        element.blur()
        suppressMarkerTooltips(500)
        if (isRoundTripMode) {
          if (group.isCluster) {
            map.easeTo({
              center: group.mapLocation.coordinates,
              zoom: Math.max(
                map.getZoom(),
                getClusterExpansionZoom(map, group.locationGroups)
              ),
              duration: 350
            })
            return
          }
          setSelectedRoundTripGroupId(group.id)
          setSelectedRoundTripOfferKey(null)
          return
        }
        clearMapSelection()
        if (group.isCluster) {
          onClusterSelect({
            ...group,
            expansionZoom: getClusterExpansionZoom(map, group.locationGroups)
          })
          return
        }
        onLocationSelect(group)
      })

      return new maplibregl.Marker({
        element,
        anchor: group.isCluster ? 'center' : 'bottom',
        offset: getMarkerOffset(group, markerGroups)
      })
        .setLngLat(group.mapLocation.coordinates)
        .addTo(map)
    })

    return () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
    }
  }, [
    clearMapSelection,
    endpointLabels,
    favoriteLocationKeys,
    focusedLocationIds,
    isMapLoaded,
    markerGroups,
    onClusterSelect,
    onLocationSelect,
    isRoundTripMode,
    renderedLocationGroups,
    roundTripEndpointLabels,
    selectedRoundTripGroupId,
    selectedGroupId,
    suppressMarkerTooltips
  ])

  const getCameraPadding = useCallback(() => {
    const map = mapRef.current
    if (!map) return 64

    const mapContainer = map.getContainer()
    const panel = offersPanelRef.current
    if (!panel || !selectedGroupId) return 64

    const containerRect = mapContainer.getBoundingClientRect()
    const panelRect = panel.getBoundingClientRect()
    const panelUsesSideLayout = window.matchMedia(
      `(min-width: ${breakpoints.m})`
    ).matches

    if (panelUsesSideLayout) {
      const desiredRightPadding = Math.ceil(
        containerRect.right - panelRect.left + 28
      )
      const maxRightPadding = Math.max(48, containerRect.width - 176)
      return {
        top: 56,
        right: Math.min(desiredRightPadding, maxRightPadding),
        bottom: 56,
        left: 56
      }
    }

    const desiredBottomPadding = Math.ceil(
      containerRect.bottom - panelRect.top + 24
    )
    const maxBottomPadding = Math.max(48, containerRect.height - 148)
    return {
      top: 48,
      right: 48,
      bottom: Math.min(desiredBottomPadding, maxBottomPadding),
      left: 48
    }
  }, [selectedGroupId])

  const updateMapCamera = useCallback(
    (duration = 500, adjustPadding = false) => {
      const map = mapRef.current
      const maplibregl = maplibreRef.current
      if (
        !map ||
        !maplibregl ||
        !isMapLoaded ||
        loading ||
        locationGroups.length === 0 ||
        selectedMapOffer
      ) {
        return
      }

      const cameraPadding = getCameraPadding()
      if (activeGroup && activeGroup.isCluster) {
        focusedGroupIdRef.current = selectedGroupId
        map.easeTo({
          center: activeGroup.mapLocation.coordinates,
          zoom: Math.max(map.getZoom(), activeGroup.expansionZoom),
          padding: cameraPadding,
          retainPadding: false,
          duration
        })
        return
      }

      const activeGroupChanged = focusedGroupIdRef.current !== selectedGroupId
      if (activeGroupChanged) {
        focusedGroupIdRef.current = selectedGroupId
        if (activeGroup) {
          map.easeTo({
            center: activeGroup.mapLocation.coordinates,
            zoom: map.getZoom(),
            padding: cameraPadding,
            retainPadding: false,
            duration
          })
          return
        }
        if (fittedLocationSignatureRef.current !== null) return
      }

      if (fittedLocationSignatureRef.current !== null) {
        if (!adjustPadding) return

        map.easeTo({
          center: activeGroup
            ? activeGroup.mapLocation.coordinates
            : map.getCenter(),
          zoom: map.getZoom(),
          padding: cameraPadding,
          retainPadding: false,
          duration
        })
        return
      }

      const bounds = new maplibregl.LngLatBounds()
      locationGroups.forEach(group =>
        bounds.extend(group.mapLocation.coordinates)
      )

      if (locationGroups.length === 1) {
        fittedLocationSignatureRef.current = locationSignature
        map.easeTo({
          center: locationGroups[0].mapLocation.coordinates,
          zoom: 9,
          padding: cameraPadding,
          retainPadding: false,
          duration
        })
        return
      }

      fittedLocationSignatureRef.current = locationSignature
      map.fitBounds(bounds, {
        padding: cameraPadding,
        maxZoom: 10,
        retainPadding: false,
        duration
      })
    },
    [
      activeGroup,
      getCameraPadding,
      isMapLoaded,
      loading,
      locationGroups,
      locationSignature,
      selectedMapOffer,
      selectedGroupId
    ]
  )

  useEffect(() => {
    const map = mapRef.current
    const maplibregl = maplibreRef.current
    if (!map || !maplibregl || !isMapLoaded) return

    const focusCoordinates = selectedOfferRoutes.reduce(
      (coordinates, route) => coordinates.concat(route.coordinates || []),
      getDirectRoute(selectedMapOffer)
    )
    if (focusCoordinates.length < 2) return

    const focusTimeout = window.setTimeout(
      () => {
        map.resize()
        map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 })
        const bounds = new maplibregl.LngLatBounds()
        focusCoordinates.forEach(coordinates => bounds.extend(coordinates))
        const camera = map.cameraForBounds(bounds, {
          padding: getCameraPadding(),
          maxZoom: 11
        })
        if (!camera) return

        map.easeTo({
          center: camera.center,
          zoom: camera.zoom,
          padding: { top: 0, right: 0, bottom: 0, left: 0 },
          retainPadding: false,
          duration: 500
        })
      },
      isDrawerOpen ? 220 : 0
    )

    return () => window.clearTimeout(focusTimeout)
  }, [
    getCameraPadding,
    isDrawerOpen,
    isMapLoaded,
    selectedMapOffer,
    selectedOfferRoutes
  ])

  useEffect(() => {
    updateMapCamera()
  }, [updateMapCamera])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!mapRef.current) return
      mapRef.current.resize()
      updateMapCamera(250, true)
    }, 220)
    return () => clearTimeout(timeout)
  }, [isDrawerOpen, updateMapCamera])

  useEffect(() => {
    const timeout = setTimeout(() => updateMapCamera(200, true), 0)
    return () => clearTimeout(timeout)
  }, [isOffersPanelCollapsed, updateMapCamera])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    let resizeTimeout
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        map.resize()
        updateMapCamera(200, true)
      }, 80)
    })
    resizeObserver.observe(map.getContainer())

    return () => {
      clearTimeout(resizeTimeout)
      resizeObserver.disconnect()
    }
  }, [isMapLoaded, updateMapCamera])

  useEffect(() => {
    if (
      !autoSelectBestRoundTrip ||
      !isRoundTripMode ||
      roundTripOffersLoading ||
      roundTripMatches.length === 0 ||
      selectedRoundTripOfferKey ||
      autoSelectedRoundTripSourceRef.current === roundTripSourceOfferId
    ) {
      return
    }

    const bestMatch = roundTripMatches[0]
    autoSelectedRoundTripSourceRef.current = roundTripSourceOfferId
    setSelectedRoundTripOfferKey(`${bestMatch.exchangeType}:${bestMatch.id}`)
    setSelectedRoundTripGroupId(null)
    onRoundTripOfferSelect(bestMatch)
  }, [
    autoSelectBestRoundTrip,
    isRoundTripMode,
    onRoundTripOfferSelect,
    roundTripMatches,
    roundTripOffersLoading,
    roundTripSourceOfferId,
    selectedRoundTripOfferKey
  ])

  useEffect(() => {
    const [request] = JSON.parse(selectedOfferRoutesKey)
    setSelectedOfferRoutes([])
    setSelectedOfferRouteIndex(0)
    if (!request) return undefined

    const controller = new AbortController()
    let isCancelled = false

    getCachedRouteDetails(
      request.endpoints[0],
      request.endpoints[1],
      controller.signal,
      'alternatives'
    )
      .then(route => {
        if (!isCancelled && route && route.coordinates.length > 1) {
          setRouteDistances(current => ({
            ...current,
            [getRouteCacheKey(...request.endpoints)]: route.distanceKm
          }))
          setSelectedOfferRoutes(
            [route, ...(route.alternates || [])].filter(
              routeOption => routeOption.coordinates.length > 1
            )
          )
        }
      })
      .catch(error => {
        if (!isCancelled && error.name !== 'AbortError') {
          setSelectedOfferRoutes([])
          setSelectedOfferRouteIndex(0)
        }
      })

    return () => {
      isCancelled = true
      controller.abort()
    }
  }, [getCachedRouteDetails, selectedOfferRoutesKey])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded) return

    const source = map.getSource(SELECTED_ROUTE_SOURCE_ID)
    if (source) {
      source.setData(
        getRoutesGeoJson(
          selectedOfferRoutes.map((route, routeIndex) => ({
            ...route,
            id: `selected-offer-route-${routeIndex}`,
            routeIndex,
            roundTrip: isRoundTripMode,
            selected: routeIndex === selectedOfferRouteIndex
          }))
        )
      )
    }
  }, [
    isMapLoaded,
    isRoundTripMode,
    selectedOfferRouteIndex,
    selectedOfferRoutes
  ])

  useEffect(() => {
    const map = mapRef.current
    const maplibregl = maplibreRef.current
    if (!map || !maplibregl || !isMapLoaded) return undefined

    const labels = createRouteDistanceLabels({
      map,
      Marker: maplibregl.Marker,
      routes: selectedOfferRoutes,
      color: getSelectedRouteColor(isRoundTripMode),
      onSelect: setSelectedOfferRouteIndex,
      getPadding: getCameraPadding
    })
    routeDistanceLabelsRef.current = labels
    return () => {
      labels.remove()
      if (routeDistanceLabelsRef.current === labels) {
        routeDistanceLabelsRef.current = null
      }
    }
  }, [getCameraPadding, isMapLoaded, isRoundTripMode, selectedOfferRoutes])

  useEffect(() => {
    if (routeDistanceLabelsRef.current) {
      routeDistanceLabelsRef.current.setSelectedRouteIndex(
        selectedOfferRouteIndex
      )
    }
  }, [
    getCameraPadding,
    isMapLoaded,
    isRoundTripMode,
    selectedOfferRouteIndex,
    selectedOfferRoutes
  ])

  const handleOfferSelect = mapOffer => {
    setFocusedDrawerMapOffer(null)
    setRoundTripSourceOfferId(null)
    setSelectedRoundTripOfferKey(null)
    setSelectedOfferId(mapOffer.id)
    onOfferSelect(mapOffer.id)
  }

  const handleShowClusterOffers = () => {
    if (!activeCluster) return

    onLocationSelect(activeCluster)
  }

  const handleShowRoundTrips = () => {
    if (!canShowRoundTripSuggestions) return

    const initialRoundTripFilters = getRoundTripFiltersForOffer(
      selectedMapOffer,
      roundTripFilters,
      roundTripCandidateTypes
    )
    setRoundTripSourceOfferId(selectedMapOffer.id)
    setSelectedRoundTripOfferKey(null)
    setSelectedRoundTripGroupId(null)
    setIsRoundTripFiltersExpanded(false)
    onRoundTripFiltersChange(initialRoundTripFilters)
    onRoundTripOffersLoad(selectedMapOffer, initialRoundTripFilters)
  }

  const updateRoundTripFilters = nextFilters => {
    preservePanelHeight()
    if (roundTripDistanceTimeoutRef.current) {
      window.clearTimeout(roundTripDistanceTimeoutRef.current)
      roundTripDistanceTimeoutRef.current = null
    }
    onRoundTripFiltersChange(nextFilters)
    setSelectedRoundTripOfferKey(null)
    setSelectedRoundTripGroupId(null)
    if (isRoundTripMode) {
      onRoundTripOffersLoad(selectedMapOffer, nextFilters)
    }
  }

  const handleRoundTripFilterConfirm = () => {
    const panelList = offersPanelListRef.current
    const filtersSection = roundTripFiltersSectionRef.current
    if (!panelList || !filtersSection) return

    const panelRect = panelList.getBoundingClientRect()
    const filtersRect = filtersSection.getBoundingClientRect()
    panelList.scrollTop = Math.max(
      0,
      panelList.scrollTop + filtersRect.top - panelRect.top
    )
  }

  const handleRoundTripDistanceChange = maxDistanceKm => {
    setRoundTripDistanceDraft(maxDistanceKm)
    if (roundTripDistanceTimeoutRef.current) {
      window.clearTimeout(roundTripDistanceTimeoutRef.current)
    }
    roundTripDistanceTimeoutRef.current = window.setTimeout(() => {
      roundTripDistanceTimeoutRef.current = null
      updateRoundTripFilters({
        ...roundTripFilters,
        maxDistanceKm
      })
    }, 500)
  }

  const handleRoundTripBack = () => {
    if (roundTripDistanceTimeoutRef.current) {
      window.clearTimeout(roundTripDistanceTimeoutRef.current)
      roundTripDistanceTimeoutRef.current = null
    }
    setRoundTripSourceOfferId(null)
    setIsRoundTripFiltersExpanded(false)
    setSelectedRoundTripOfferKey(null)
    setSelectedRoundTripGroupId(null)
    onOfferSelect(selectedMapOffer.id)
  }

  const handleRoundTripOfferSelect = mapOffer => {
    setSelectedRoundTripOfferKey(`${mapOffer.exchangeType}:${mapOffer.id}`)
    setSelectedRoundTripGroupId(null)
    onRoundTripOfferSelect(mapOffer)
  }

  const renderOfferRouteDistance = mapOffer => {
    const endpoints = getDirectRoute(mapOffer)
    if (endpoints.length < 2) return null

    const cacheKey = getRouteCacheKey(endpoints[0], endpoints[1])
    const distanceKm = routeDistances[cacheKey]
    if (!Number.isFinite(distanceKm)) return null

    return (
      <OfferRouteDistance
        title={intl.formatMessage({ id: 'offers.mapRouteDistance' })}
      >
        {`${Math.round(distanceKm)} km`}
      </OfferRouteDistance>
    )
  }

  const renderOfferReservations = mapOffer => {
    if (!mapOffer.offer || mapOffer.offer.type !== OFFER_TYPES.QUEUE) {
      return null
    }

    const reservationsCount = Math.max(
      0,
      Number(mapOffer.offer.reservationsCount) || 0
    )
    if (reservationsCount === 0) return null
    const reservationsValue = `${reservationsCount}/${MAX_RESERVATIONS_COUNT_FOR_QUEUE}`
    const reservationsLabel = `${intl.formatMessage({
      id: 'stocksHeaders.reservations'
    })}: ${reservationsValue}`

    return (
      <ReservationsBadge
        aria-label={reservationsLabel}
        title={reservationsLabel}
      >
        {reservationsValue}
      </ReservationsBadge>
    )
  }

  const panelRole =
    !isRoundTripMode && activeGroup ? activeGroup.role : 'origin'
  let panelOffersCount = null
  if (isRoundTripMode) {
    panelOffersCount = roundTripMatches.length
  } else if (activeGroup) {
    panelOffersCount =
      pagination && pagination.total != null
        ? pagination.total
        : activeGroup.offersCount
  }
  const pageLoading = isRoundTripMode ? roundTripOffersLoading : offersLoading
  const handleDisplayedPageChange = page => {
    if (isRoundTripMode) {
      setRoundTripPage(page)
      setSelectedRoundTripOfferKey(null)
      setSelectedRoundTripGroupId(null)
      return
    }
    if (onPageChange) onPageChange(page)
  }

  return (
    <div
      className={className}
      data-round-trip-active={isRoundTripMode || roundTripOnly || undefined}
    >
      <MapCanvas ref={mapContainerRef} />
      {showPageNavigation && (
        <MapPageNavigation
          data-panel-visible={activeGroup || isRoundTripMode ? 'true' : 'false'}
          aria-label={intl.formatMessage({ id: 'offers.mapPagination' })}
        >
          <MapPageButton
            type='button'
            disabled={pageLoading || currentPage <= 1}
            aria-label={intl.formatMessage({ id: 'offers.mapPreviousPage' })}
            onClick={() => handleDisplayedPageChange(currentPage - 1)}
          >
            ‹
          </MapPageButton>
          <MapPageSummary aria-live='polite'>
            {intl.formatMessage(
              { id: 'offers.mapPageSummary' },
              {
                currentPage,
                from: displayedPagination.from,
                lastPage,
                to: displayedPagination.to,
                total: displayedPagination.total
              }
            )}
          </MapPageSummary>
          <MapPageButton
            type='button'
            disabled={pageLoading || currentPage >= lastPage}
            aria-label={intl.formatMessage({ id: 'offers.mapNextPage' })}
            onClick={() => handleDisplayedPageChange(currentPage + 1)}
          >
            ›
          </MapPageButton>
        </MapPageNavigation>
      )}
      {loading && (
        <MapStatus role='status'>
          {intl.formatMessage({ id: 'common.loading' })}
        </MapStatus>
      )}
      {(mapError || offersError) && (
        <MapStatus role='alert'>
          {intl.formatMessage({ id: 'offers.mapLoadError' })}
        </MapStatus>
      )}
      {!initialRoundTripOffer &&
        !isRoundTripMode &&
        !loading &&
        !mapError &&
        !offersError &&
        locationGroups.length === 0 && (
          <MapStatus role='status'>
            {mapOffersCount === 0 ? (
              <NoOffersMessage />
            ) : (
              intl.formatMessage({ id: 'offers.mapNoLocations' })
            )}
          </MapStatus>
        )}
      <Legend>
        <LegendToggle
          type='button'
          aria-expanded={isLegendExpanded}
          onClick={() => setIsLegendExpanded(expanded => !expanded)}
        >
          <LegendTitle>
            {intl.formatMessage({ id: 'offers.mapLegend' })}
          </LegendTitle>
          <LegendToggleIcon aria-hidden='true'>
            {isLegendExpanded ? '−' : '+'}
          </LegendToggleIcon>
        </LegendToggle>
        {isLegendExpanded && (
          <LegendBody>
            {isRoundTripMode
              ? ROUND_TRIP_ENDPOINT_ROLES.map(role => (
                  <LegendItem key={role}>
                    <LegendMarker roleType={role} aria-hidden='true' />
                    {roundTripEndpointLabels[role]}
                  </LegendItem>
                ))
              : layerKeys.map(layerKey => (
                  <LegendItem key={layerKey}>
                    <LegendMarker roleType={layerKey} aria-hidden='true' />
                    {endpointLabels[layerKey]}
                  </LegendItem>
                ))}
          </LegendBody>
        )}
      </Legend>
      {(activeGroup || isRoundTripMode) && (
        <OffersPanel
          ref={offersPanelRef}
          collapsed={isOffersPanelCollapsed}
          preservedHeight={preservedPanelHeight}
          roundTripOnly={roundTripOnly}
        >
          <PanelHeader collapsed={isOffersPanelCollapsed}>
            <PanelHeading>
              <PanelRole roleType={panelRole} aria-hidden='true' />
              <PanelCount roundTripOnly={roundTripOnly}>
                <OfferAgeFilterCount count={panelOffersCount} />
              </PanelCount>
            </PanelHeading>
            <PanelActions>
              <PanelCollapseButton
                type='button'
                aria-expanded={!isOffersPanelCollapsed}
                aria-label={intl.formatMessage({
                  id: isOffersPanelCollapsed
                    ? 'offers.mapExpandOfferList'
                    : 'offers.mapCollapseOfferList'
                })}
                onClick={() =>
                  onPanelStateChange(previous => ({
                    ...previous,
                    collapsed: !previous.collapsed
                  }))
                }
              >
                {isOffersPanelCollapsed ? '+' : '−'}
              </PanelCollapseButton>
              {!roundTripOnly && (
                <CloseButton
                  type='button'
                  onClick={handleSelectionClear}
                  aria-label={intl.formatMessage({ id: 'common.close' })}
                >
                  ×
                </CloseButton>
              )}
            </PanelActions>
          </PanelHeader>
          {!isOffersPanelCollapsed && activeCluster && !isClusterListVisible && (
            <ClusterListAction type='button' onClick={handleShowClusterOffers}>
              {intl.formatMessage({ id: 'offers.mapShowOfferList' })}
            </ClusterListAction>
          )}
          {!isOffersPanelCollapsed && (!activeCluster || isClusterListVisible) && (
            <>
              {canShowRoundTripSuggestions && !isRoundTripMode && (
                <RoundTripActionContainer>
                  <RoundTripAction type='button' onClick={handleShowRoundTrips}>
                    <RoundTripActionIcon aria-hidden='true'>
                      ↩
                    </RoundTripActionIcon>
                    <span>
                      {intl.formatMessage({ id: 'offers.mapRoundTripShow' })}
                    </span>
                  </RoundTripAction>
                </RoundTripActionContainer>
              )}
              <PanelList
                ref={offersPanelListRef}
                onScroll={event => {
                  if (
                    isRoundTripMode ||
                    (offersLoading && offers.length === 0)
                  ) {
                    return
                  }
                  const { scrollTop } = event.currentTarget
                  onPanelStateChange(previous =>
                    previous.scrollTop === scrollTop
                      ? previous
                      : { ...previous, scrollTop }
                  )
                }}
                data-round-trip-active={
                  isRoundTripMode || roundTripOnly || undefined
                }
                loading={!isRoundTripMode && offersLoading}
              >
                {!isRoundTripMode && (
                  <OfferResults>
                    {mapOffers.map(mapOffer => (
                      <OfferCard
                        key={mapOffer.id}
                        dimmed={Boolean(
                          visibleMapOfferId != null &&
                            String(visibleMapOfferId) !== String(mapOffer.id)
                        )}
                      >
                        <OfferCardButton
                          role='button'
                          tabIndex={0}
                          onClick={() => handleOfferSelect(mapOffer)}
                          onKeyDown={event =>
                            handleOfferCardKeyDown(event, () =>
                              handleOfferSelect(mapOffer)
                            )
                          }
                        >
                          <OfferCardHeader>
                            <OfferReference>
                              {mapOffer.referenceNumber}
                            </OfferReference>
                            <MapFavoriteButton
                              active={isFavorite(
                                mapOffer.offer.offerType,
                                mapOffer.id
                              )}
                              onClick={() => toggleFavorite(mapOffer.offer)}
                            />
                            <OfferHeaderDetails>
                              {renderOfferReservations(mapOffer)}
                              {renderOfferRouteDistance(mapOffer)}
                            </OfferHeaderDetails>
                          </OfferCardHeader>
                          <MapOfferRoute
                            from={mapOffer.from}
                            to={mapOffer.to}
                          />
                          <OfferProperties>
                            {mapOffer.containerName && (
                              <OfferProperty>
                                <ContainerIcon />
                                <span>{mapOffer.containerName}</span>
                              </OfferProperty>
                            )}
                            {mapOffer.shipownerName && (
                              <OfferProperty>
                                <ShipIcon />
                                <span>{mapOffer.shipownerName}</span>
                              </OfferProperty>
                            )}
                            {mapOffer.companyName && (
                              <OfferProperty>
                                <CompanyIcon />
                                <span>{mapOffer.companyName}</span>
                              </OfferProperty>
                            )}
                          </OfferProperties>
                        </OfferCardButton>
                      </OfferCard>
                    ))}
                    {offersLoading && (
                      <OfferListLoadingOverlay role='status'>
                        <CircularProgress size={30} />
                      </OfferListLoadingOverlay>
                    )}
                    {!offersLoading && offersListError && (
                      <OfferListStatus role='alert' error>
                        {intl.formatMessage({ id: 'offers.mapListLoadError' })}
                      </OfferListStatus>
                    )}
                    {!offersLoading &&
                      !offersListError &&
                      mapOffers.length === 0 && (
                        <OfferListStatus role='status' empty>
                          <NoOffersMessage />
                        </OfferListStatus>
                      )}
                  </OfferResults>
                )}
                {isRoundTripMode && (
                  <RoundTripSection>
                    <RoundTripContent loading={roundTripOffersLoading}>
                      {!roundTripOnly && (
                        <RoundTripSourceOfferButton
                          type='button'
                          onClick={handleRoundTripBack}
                        >
                          <OfferCardHeader>
                            <OfferReference>
                              {selectedMapOffer.referenceNumber}
                            </OfferReference>
                            <OfferHeaderDetails>
                              {renderOfferReservations(selectedMapOffer)}
                              {renderOfferRouteDistance(selectedMapOffer)}
                            </OfferHeaderDetails>
                          </OfferCardHeader>
                          <MapOfferRoute
                            from={selectedMapOffer.from}
                            to={selectedMapOffer.to}
                          />
                          <OfferProperties>
                            {selectedMapOffer.containerName && (
                              <OfferProperty>
                                <ContainerIcon />
                                <span>{selectedMapOffer.containerName}</span>
                              </OfferProperty>
                            )}
                            {selectedMapOffer.shipownerName && (
                              <OfferProperty>
                                <ShipIcon />
                                <span>{selectedMapOffer.shipownerName}</span>
                              </OfferProperty>
                            )}
                            {selectedMapOffer.companyName && (
                              <OfferProperty>
                                <CompanyIcon />
                                <span>{selectedMapOffer.companyName}</span>
                              </OfferProperty>
                            )}
                          </OfferProperties>
                          <RoundTripSourceAction>
                            <span aria-hidden='true'>←</span>
                            {intl.formatMessage(
                              { id: 'offers.mapRoundTripBack' },
                              { referenceNumber: '' }
                            )}
                          </RoundTripSourceAction>
                        </RoundTripSourceOfferButton>
                      )}
                      <RoundTripFiltersSection ref={roundTripFiltersSectionRef}>
                        <RoundTripFiltersToggle
                          type='button'
                          aria-expanded={isRoundTripFiltersExpanded}
                          aria-label={intl.formatMessage({
                            id: 'common.filters'
                          })}
                          title={intl.formatMessage({ id: 'common.filters' })}
                          onClick={() => {
                            resetPanelHeight()
                            setIsRoundTripFiltersExpanded(expanded => !expanded)
                          }}
                        >
                          <RoundTripFiltersLabel>
                            {intl.formatMessage({ id: 'common.filters' })}
                          </RoundTripFiltersLabel>
                          <FilterListIcon aria-hidden='true' />
                        </RoundTripFiltersToggle>
                        {isRoundTripFiltersExpanded && (
                          <RoundTripFilters>
                            <RoundTripSelect
                              dropdownBoundaryRef={offersPanelRef}
                              fitDropdownToBoundary
                              stickyActions
                              id='round-trip-shipowner'
                              name='shipowner'
                              label={intl.formatMessage({
                                id: 'offersFilters.shipowner'
                              })}
                              value={roundTripFilters.shipowner || []}
                              options={roundTripShipownerOptions}
                              onConfirm={handleRoundTripFilterConfirm}
                              onDropdownClose={resetPanelHeight}
                              onChange={(name, value) =>
                                updateRoundTripFilters({
                                  ...roundTripFilters,
                                  maxDistanceKm: roundTripDistanceDraft,
                                  [name]: value
                                })
                              }
                              placeholder={intl.formatMessage({
                                id: 'offersFilters.searchPlaceholder'
                              })}
                            />
                            <RoundTripSelect
                              dropdownBoundaryRef={offersPanelRef}
                              fitDropdownToBoundary
                              stickyActions
                              id='round-trip-container'
                              name='container'
                              label={intl.formatMessage({
                                id: 'orders.containerType'
                              })}
                              value={roundTripFilters.container || []}
                              options={roundTripContainerOptions}
                              onConfirm={handleRoundTripFilterConfirm}
                              onDropdownClose={resetPanelHeight}
                              onChange={(name, value) =>
                                updateRoundTripFilters({
                                  ...roundTripFilters,
                                  maxDistanceKm: roundTripDistanceDraft,
                                  [name]: value
                                })
                              }
                              placeholder={intl.formatMessage({
                                id: 'offersFilters.searchPlaceholder'
                              })}
                            />
                            <RoundTripAddedFilter
                              stackDateTime
                              id='round-trip-added'
                              dropdownBoundaryRef={offersPanelRef}
                              range={getOfferDateRangeFromFilters(
                                roundTripFilters
                              )}
                              onDropdownClose={resetPanelHeight}
                              onChange={range => {
                                updateRoundTripFilters({
                                  ...roundTripFilters,
                                  maxDistanceKm: roundTripDistanceDraft,
                                  ...getOfferDateRangeFilters(range)
                                })
                                handleRoundTripFilterConfirm()
                              }}
                            />
                            {roundTripCandidateTypes.map(
                              candidateExchangeType => {
                                const active = roundTripFilters.exchangeTypes.includes(
                                  candidateExchangeType
                                )
                                return (
                                  <RoundTripCheckboxLabel
                                    key={candidateExchangeType}
                                  >
                                    <input
                                      type='checkbox'
                                      checked={active}
                                      onChange={() => {
                                        const nextExchangeTypes = active
                                          ? roundTripFilters.exchangeTypes.filter(
                                              exchangeTypeValue =>
                                                exchangeTypeValue !==
                                                candidateExchangeType
                                            )
                                          : Array.from(
                                              new Set([
                                                ...roundTripFilters.exchangeTypes,
                                                candidateExchangeType
                                              ])
                                            )
                                        updateRoundTripFilters({
                                          ...roundTripFilters,
                                          maxDistanceKm: roundTripDistanceDraft,
                                          exchangeTypes: nextExchangeTypes
                                        })
                                      }}
                                    />
                                    <RoundTripFilterCheckbox
                                      selected={active}
                                    />
                                    <span>
                                      {intl.formatMessage({
                                        id:
                                          ROUND_TRIP_TYPE_LABEL_IDS[
                                            candidateExchangeType
                                          ]
                                      })}
                                    </span>
                                  </RoundTripCheckboxLabel>
                                )
                              }
                            )}
                            <RoundTripCheckboxLabel>
                              <input
                                type='checkbox'
                                checked={roundTripFilters.daysAhead === 1}
                                onChange={event =>
                                  updateRoundTripFilters({
                                    ...roundTripFilters,
                                    maxDistanceKm: roundTripDistanceDraft,
                                    daysAhead: event.target.checked ? 1 : 0
                                  })
                                }
                              />
                              <RoundTripFilterCheckbox
                                selected={roundTripFilters.daysAhead === 1}
                              />
                              <span>
                                {intl.formatMessage({
                                  id: 'offers.mapRoundTripNextDay'
                                })}
                              </span>
                            </RoundTripCheckboxLabel>
                            <RoundTripRangeControl>
                              <RoundTripRangeStepper
                                step={25}
                                minValue={0}
                                maxValue={300}
                                unit='km'
                                value={roundTripDistanceDraft}
                                onChange={handleRoundTripDistanceChange}
                              />
                            </RoundTripRangeControl>
                          </RoundTripFilters>
                        )}
                      </RoundTripFiltersSection>
                      <OfferResults>
                        {!roundTripOnly &&
                          !roundTripOffersLoading &&
                          roundTripMatches.length > 0 && (
                            <RoundTripOffersCount>
                              <OfferAgeFilterCount
                                count={roundTripMatches.length}
                              />
                            </RoundTripOffersCount>
                          )}
                        <RoundTripRefreshNotice
                          failedExchangeTypes={roundTripFailedExchangeTypes}
                          {...roundTripRefreshStatus}
                        />
                        {!roundTripOffersLoading && roundTripOffersError && (
                          <RoundTripStatus error>
                            {intl.formatMessage({
                              id: 'offers.mapRoundTripLoadError'
                            })}
                          </RoundTripStatus>
                        )}
                        {!roundTripOffersLoading &&
                          !roundTripOffersError &&
                          displayedRoundTripMatches.length === 0 && (
                            <RoundTripStatus empty>
                              {intl.formatMessage({
                                id: 'offers.mapRoundTripEmpty'
                              })}
                            </RoundTripStatus>
                          )}
                        {displayedRoundTripMatches.map(match => (
                          <OfferCard
                            key={`${match.exchangeType}:${match.id}`}
                            isNewLive={
                              roundTripOnly &&
                              Boolean(
                                roundTripLiveOfferTimes[
                                  getRoundTripOfferKey(match)
                                ]
                              )
                            }
                            dimmed={Boolean(
                              visibleRoundTripOfferKey &&
                                visibleRoundTripOfferKey !==
                                  `${match.exchangeType}:${match.id}`
                            )}
                          >
                            <OfferCardButton
                              role='button'
                              tabIndex={0}
                              onClick={() => handleRoundTripOfferSelect(match)}
                              onKeyDown={event =>
                                handleOfferCardKeyDown(event, () =>
                                  handleRoundTripOfferSelect(match)
                                )
                              }
                            >
                              <OfferCardHeader>
                                <OfferReference>
                                  {match.referenceNumber}
                                </OfferReference>
                                <MapFavoriteButton
                                  active={isFavorite(
                                    match.offer.offerType,
                                    match.id
                                  )}
                                  onClick={() => toggleFavorite(match.offer)}
                                />
                                <OfferHeaderDetails>
                                  {renderOfferReservations(match)}
                                  {renderOfferRouteDistance(match)}
                                </OfferHeaderDetails>
                              </OfferCardHeader>
                              <MapOfferRoute
                                from={match.from}
                                to={match.to}
                                roundTrip
                              />
                              <OfferProperties>
                                <OfferProperty>
                                  <span>
                                    {intl.formatMessage({
                                      id: 'offers.mapRoundTripDistance'
                                    })}
                                    : {Math.round(match.roundTripDistanceKm)} km
                                  </span>
                                </OfferProperty>
                                {match.containerName && (
                                  <OfferProperty>
                                    <ContainerIcon />
                                    <span>{match.containerName}</span>
                                  </OfferProperty>
                                )}
                                {match.companyName && (
                                  <OfferProperty>
                                    <CompanyIcon />
                                    <span>{match.companyName}</span>
                                  </OfferProperty>
                                )}
                              </OfferProperties>
                            </OfferCardButton>
                          </OfferCard>
                        ))}
                      </OfferResults>
                    </RoundTripContent>
                    {roundTripOffersLoading && (
                      <RoundTripLoadingOverlay role='status'>
                        <CircularProgress size={30} />
                      </RoundTripLoadingOverlay>
                    )}
                  </RoundTripSection>
                )}
              </PanelList>
            </>
          )}
        </OffersPanel>
      )}
    </div>
  )
}

const MapCanvas = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  isolation: isolate;
`

const MapPageNavigation = styled.nav`
  position: absolute;
  top: 0.8rem;
  left: 50%;
  z-index: 5;
  display: flex;
  min-height: 34px;
  align-items: center;
  transform: translateX(-50%);
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.65).hex()};
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 2px 10px rgba(36, 45, 63, 0.16);
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};

  ${breakpoint.m`
    &[data-panel-visible='true'] {
      left: calc(50% - 147px);
    }
  `}
`

const MapPageButton = styled.button`
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: ${({ theme }) => theme.colors.catskillWhite.hex()};
  }
  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`

const MapPageSummary = styled.span`
  padding: 0 0.35rem;
  font-size: 0.67rem;
  font-weight: 600;
  white-space: nowrap;
`

const MapStatus = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 4;
  max-width: 340px;
  padding: 0.85rem 1.15rem;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 16px rgba(36, 45, 63, 0.18);
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
`

const Legend = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  z-index: 3;
  display: none;
  padding: 0.62rem 0.75rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 14px rgba(36, 45, 63, 0.15);
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.7rem;
  ${breakpoint.s`
    display: block;
  `}
`

const LegendToggle = styled.button`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
`

const LegendTitle = styled.span`
  font-weight: 700;
`

const LegendToggleIcon = styled.span`
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-left: 0.8rem;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 1rem;
  line-height: 1;
`

const LegendBody = styled.div`
  min-width: 150px;
  margin-top: 0.55rem;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  min-height: 24px;
  & + & {
    margin-top: 0.35rem;
  }
`

const roleColors = css`
  ${({ roleType, roundTripExchangeType, theme }) => {
    if (roundTripExchangeType === EXCHANGE_MAP_TYPES.EXPORT) {
      return css`
        background: ${ROUND_TRIP_EXPORT_COLOR};
      `
    }
    if (roundTripExchangeType === EXCHANGE_MAP_TYPES.GENERAL) {
      return css`
        background: #ef8b2c;
      `
    }
    if (roleType === 'origin') {
      return css`
        background: ${theme.colors.apple.hex()};
      `
    }
    if (roleType === 'return') {
      return css`
        background: ${ROUND_TRIP_ROUTE_COLOR};
      `
    }
    return css`
      background: ${theme.colors.easternBlue.hex()};
    `
  }}
`

const markerPinShape = css`
  ${roleColors};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  border-radius: 50% 50% 50% 6px;
  box-sizing: border-box;
  color: white;
  filter: drop-shadow(0 2px 3px rgba(36, 45, 63, 0.24));
  transform: rotate(-45deg);
  > span {
    transform: rotate(45deg);
  }
`

const LegendMarker = styled.span`
  ${markerPinShape};
  width: 26px;
  height: 26px;
  margin: 3px 0.75rem 3px 3px;
  border-width: 2px;
`

const OffersPanel = styled.aside`
  position: absolute;
  right: 0.8rem;
  bottom: 0.8rem;
  left: 0.8rem;
  z-index: 10;
  display: flex;
  box-sizing: border-box;
  min-height: ${({ collapsed, preservedHeight }) =>
    !collapsed && preservedHeight ? `min(${preservedHeight}px, 62%)` : '0'};
  max-height: ${({ collapsed }) => (collapsed ? 'none' : '62%')};
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.6).hex()};
  border-radius: ${({ roundTripOnly }) => (roundTripOnly ? '0' : '6px')};
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 6px 24px rgba(36, 45, 63, 0.2);
  ${breakpoint.m`
    top: 0.8rem;
    right: 0.8rem;
    bottom: auto;
    left: auto;
    width: 280px;
    max-width: calc(100% - 1.6rem);
    min-height: ${({ collapsed, preservedHeight }) =>
      !collapsed && preservedHeight
        ? `min(${preservedHeight}px, calc(100% - 1.6rem))`
        : '0'};
    max-height: ${({ collapsed }) =>
      collapsed ? 'none' : 'calc(100% - 1.6rem)'};
  `}
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.65rem;
  border-bottom: ${({ collapsed, theme }) =>
    collapsed ? '0' : `1px solid ${theme.colors.ghost.alpha(0.45).hex()}`};
`

const PanelHeading = styled.div`
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
`

const PanelRole = styled.span`
  ${markerPinShape};
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  margin: 3px 0.65rem 3px 3px;
  border-width: 2px;
`

const PanelCount = styled.div`
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.72rem;
  font-weight: ${({ roundTripOnly }) => (roundTripOnly ? 400 : 600)};

  strong {
    font-weight: 700;
  }
`

const PanelActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.35rem;
`

const PanelControlButton = styled.button`
  flex: 0 0 auto;
  padding: 0 0.25rem;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  line-height: 1;
`

const PanelCollapseButton = styled(PanelControlButton)`
  font-size: 1.25rem;
`

const CloseButton = styled(PanelControlButton)`
  font-size: 1.45rem;
`

const PanelList = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: ${({ loading }) => (loading ? '230px' : '0')};
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 0.7rem;

  &[data-round-trip-active='true'] {
    margin: 0 0 0.7rem;
    padding: 0 0.7rem;
    scrollbar-gutter: stable;
  }
`

const OfferResults = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
`

const ClusterListAction = styled.button`
  width: 100%;
  padding: 0.78rem 0.9rem;
  border: 0;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.catskillWhite.hex()};
  }
`

const OfferCard = styled.div`
  position: relative;
  width: 100%;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.55).hex()};
  box-sizing: border-box;
  filter: ${({ dimmed }) => (dimmed ? 'grayscale(0.35)' : 'none')};
  opacity: ${({ dimmed }) => (dimmed ? 0.55 : 1)};
  ${({ isNewLive, theme }) =>
    isNewLive &&
    css`
      border-left: 4px solid ${theme.colors.apple.hex()};
    `}
`

const OfferCardButton = styled.div`
  display: block;
  width: 100%;
  padding: 0.8rem 0;
  border: 0;
  background: transparent;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.easternBlue.hex()};
    outline-offset: -2px;
  }
`

const MapFavoriteButton = styled(FavoriteOfferButton)`
  flex: 0 0 auto;
  margin-left: 0.1rem;
`

const RoundTripAction = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.apple.hex()};
  border-radius: 0;
  background: ${({ theme }) => theme.colors.white.hex()};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 0.72rem;
  font-weight: 800;
`

const RoundTripActionContainer = styled.div`
  flex: 0 0 auto;
  margin: 0;
  padding: 0.75rem 0.7rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
  background: ${({ theme }) => theme.colors.white.hex()};
`

const OfferListLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.58);
  backdrop-filter: blur(2px);
  color: ${({ theme }) => theme.colors.apple.hex()};
  .MuiCircularProgress-colorPrimary {
    color: inherit;
  }
`

const RoundTripActionIcon = styled.span`
  margin-right: 0.45rem;
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 1rem;
  line-height: 1;
`

const RoundTripSection = styled.section`
  position: relative;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  width: 100%;
`

const RoundTripContent = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  min-height: ${({ loading }) => (loading ? '210px' : '0')};
  filter: ${({ loading }) => (loading ? 'blur(2px)' : 'none')};
  opacity: ${({ loading }) => (loading ? 0.42 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
`

const RoundTripLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  min-height: 210px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.apple.hex()};
  .MuiCircularProgress-colorPrimary {
    color: inherit;
  }
`

const RoundTripFilters = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.55rem;
  padding: 0.65rem 0;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
`

const RoundTripSelect = styled(Select)`
  width: 100%;

  > div:first-child {
    width: 100%;
  }
`

const RoundTripAddedFilter = styled(OfferAgeFilter)`
  width: 100%;
  min-width: 0;

  > div:first-child {
    width: 100%;
  }
`

const RoundTripFiltersSection = styled.div`
  flex: 0 0 auto;
  margin: 0 0 0.55rem;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.55).hex()};
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.55).hex()};

  &:first-child {
    border-top: 0;
  }
`

const RoundTripFiltersLabel = styled.span`
  color: inherit;
  font-size: 0.68rem;
  font-weight: 700;
`

const RoundTripFiltersToggle = styled.button`
  display: flex;
  width: 100%;
  height: 32px;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.corduroy.hex()};

  &[aria-expanded='true'] {
    color: ${({ theme }) => theme.colors.apple.hex()};
  }

  svg {
    width: 18px;
    height: 18px;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.apple.alpha(0.3).hex()};
    outline-offset: -2px;
  }
`

const RoundTripCheckboxLabel = styled.label`
  display: flex;
  min-width: 0;
  height: 26px;
  align-items: center;
  gap: 0.45rem;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 700;
  input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
  }
  input:focus-visible + span {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.apple.alpha(0.2).hex()};
  }
`

const RoundTripFilterCheckbox = styled(FilterCheckbox)`
  flex: 0 0 auto;
  margin-right: 0;
`

const RoundTripRangeControl = styled.div`
  position: relative;
  display: flex;
  width: 95px;
  height: 24px;
  flex: 0 0 95px;
  align-items: center;
  justify-self: start;
  box-sizing: border-box;
`

const RoundTripRangeStepper = styled(RangeStepper)`
  right: 0;
`

const RoundTripSourceOfferButton = styled.button`
  display: block;
  width: 100%;
  margin: 0.55rem 0;
  padding: 0.7rem 0.75rem 0;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.7).hex()};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.catskillWhite.alpha(0.55).hex()};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.apple.alpha(0.35).hex()};
    outline-offset: -2px;
  }
`

const ReservationsBadge = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
`

const RoundTripSourceAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin: 0.7rem -0.75rem 0;
  padding: 0.48rem 0.65rem;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.55).hex()};
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  font-size: 0.64rem;
  font-weight: 700;
`

const RoundTripOffersCount = styled.div`
  margin: 0.55rem 0 0.15rem;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.7rem;
`

const RoundTripStatus = styled.div`
  margin: 0.55rem 0 0.75rem;
  padding: 0.6rem;
  border-radius: 4px;
  background: ${({ empty, error }) => {
    if (empty) return 'transparent'
    return error ? 'rgba(226, 73, 73, 0.09)' : 'rgba(239, 139, 44, 0.08)'
  }};
  color: ${({ empty, error, theme }) => {
    if (empty) return theme.colors.ebonyClay.hex()
    return error ? theme.colors.error.hex() : theme.colors.corduroy.hex()
  }};
  font-size: 0.67rem;
  font-weight: ${({ empty }) => (empty ? 600 : 400)};
  line-height: 1.4;
  text-align: ${({ empty }) => (empty ? 'center' : 'left')};
  ${({ empty }) =>
    empty &&
    css`
      display: flex;
      min-height: 88px;
      flex: 1 0 auto;
      align-items: center;
      justify-content: center;
      margin: 0;
      box-sizing: border-box;
    `}
`

const OfferListStatus = styled(RoundTripStatus)`
  margin: 0;
`

const OfferCardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.45rem;
`

const OfferHeaderDetails = styled.span`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.28rem;
  margin-left: auto;
`

const OfferRouteDistance = styled.span`
  display: inline-flex;
  min-width: 2.8rem;
  align-items: center;
  justify-content: flex-end;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.68).hex()};
  font-size: 0.66rem;
  font-weight: 600;
  white-space: nowrap;

  .MuiCircularProgress-root {
    color: ${({ theme }) => theme.colors.apple.hex()};
  }
`

const OfferReference = styled.span`
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  font-size: 0.78rem;
  font-weight: 700;
`

const OfferRoute = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.72rem;
  font-weight: 600;
`

const OfferEndpoint = styled.div`
  display: flex;
  min-width: 0;
  flex: ${({ preserveName }) => (preserveName ? '0 0 auto' : '0 1 auto')};
  align-items: baseline;
  gap: 0.3rem;
`

const LocationName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const OfferRouteDate = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.64rem;
  font-weight: 400;
  white-space: nowrap;
`

const RouteArrow = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 1.05rem;
  font-weight: 700;
`

const RoundTripArrow = styled(RouteArrow)``

const OfferProperties = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem 0.7rem;
  margin-top: 0.45rem;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.65rem;
`

const OfferProperty = styled.span`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 0.25rem;
  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    opacity: 0.72;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export default styled(ExchangeOffersMap)`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: ${({ compact }) => (compact ? '420px' : 'calc(100vh - 220px)')};
  min-height: ${({ compact }) => (compact ? '420px' : '510px')};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
  border-radius: ${({ roundTripOnly }) => (roundTripOnly ? '0' : '5px')};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.catskillWhite.hex()};

  &[data-round-trip-active='true'] {
    border-radius: 0;
  }

  &[data-round-trip-active='true']
    ${MapPageNavigation},
    &[data-round-trip-active='true']
    ${MapStatus},
    &[data-round-trip-active='true']
    ${Legend},
    &[data-round-trip-active='true']
    ${OffersPanel},
    &[data-round-trip-active='true']
    ${RoundTripSourceOfferButton},
    &[data-round-trip-active='true']
    ${RoundTripStatus} {
    border-radius: 0;
  }

  &[data-round-trip-active='true'] .maplibregl-ctrl-group,
  &[data-round-trip-active='true'] .maplibregl-ctrl-group button:first-child,
  &[data-round-trip-active='true'] .maplibregl-ctrl-group button:last-child {
    border-radius: 0;
  }

  &[data-round-trip-active='true'] .maplibregl-ctrl-group:not(:empty) {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.ghost.alpha(0.6).hex()};
  }

  .maplibregl-map {
    font-family: 'Open Sans', sans-serif;
  }

  ${ROUTE_DISTANCE_LABEL_STYLES}

  .maplibregl-ctrl-top-left,
  .maplibregl-ctrl-top-right,
  .maplibregl-ctrl-bottom-left,
  .maplibregl-ctrl-bottom-right {
    z-index: ${MAP_UI_PRIORITY};
  }

  @media (max-width: 1024px) {
    .maplibregl-ctrl-bottom-right {
      bottom: 3.5rem;
    }
  }

  .maplibregl-ctrl-attrib {
    font-size: 9px;
  }

  .exchange-offers-visibility-control button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ roundTripOnly }) =>
    roundTripOnly &&
    css`
      .exchange-offers-visibility-control {
        display: none;
      }
    `}

  .exchange-offers-favorites-control button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .exchange-offers-favorites-control__star {
    color: ${({ theme }) => theme.colors.corduroy.alpha(0.65).hex()};
    font-size: 1.25rem;
    line-height: 1;
    transition: color 0.15s ease, transform 0.15s ease;
  }

  .exchange-offers-favorites-control
    button:hover
    .exchange-offers-favorites-control__star,
  .exchange-offers-favorites-control
    button:focus-visible
    .exchange-offers-favorites-control__star {
    color: ${({ theme }) => theme.colors.corduroy.hex()};
  }

  .exchange-offers-favorites-control
    button[data-active='true']
    .exchange-offers-favorites-control__star {
    color: #f4b400;
    transform: scale(1.08);
  }

  .exchange-offers-visibility-control__marker {
    display: block;
    width: 18px;
    height: 18px;
    border: 2px solid white;
    border-radius: 50% 50% 50% 5px;
    box-sizing: border-box;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.apple.hex()} 0 33%,
      ${({ theme }) => theme.colors.easternBlue.hex()} 33% 66%,
      #7358b6 66% 100%
    );
    filter: drop-shadow(0 1px 2px rgba(36, 45, 63, 0.24));
    transform: rotate(-45deg);
  }

  .exchange-offers-visibility-control
    button[data-visibility='origin']
    .exchange-offers-visibility-control__marker {
    background: ${({ theme }) => theme.colors.apple.hex()};
  }

  .exchange-offers-visibility-control
    button[data-visibility='destination']
    .exchange-offers-visibility-control__marker {
    background: ${({ theme }) => theme.colors.easternBlue.hex()};
  }

  .exchange-offers-visibility-control
    button[data-visibility='return']
    .exchange-offers-visibility-control__marker {
    background: #7358b6;
  }

  .exchange-offers-marker {
    position: absolute;
    width: 28px;
    height: 30px;
    padding: 0;
    border: 0;
    background: transparent;
    filter: drop-shadow(0 2px 3px rgba(36, 45, 63, 0.24));
  }

  .exchange-offers-marker__pin {
    position: absolute;
    bottom: 2px;
    left: 3px;
    display: flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    box-sizing: border-box;
    color: white;
    transition: filter 0.15s ease, opacity 0.15s ease;
  }

  .exchange-offers-marker--origin .exchange-offers-marker__pin,
  .exchange-offers-marker--destination .exchange-offers-marker__pin,
  .exchange-offers-marker--return .exchange-offers-marker__pin {
    border-radius: 50% 50% 50% 6px;
    transform: rotate(-45deg);
  }

  .exchange-offers-marker--cluster {
    width: 34px;
    height: 34px;
  }

  .exchange-offers-marker--cluster .exchange-offers-marker__pin {
    top: 0;
    right: 0;
    bottom: auto;
    left: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.ebonyClay.hex()};
    transform: none;
  }

  .exchange-offers-marker--cluster .exchange-offers-marker__count {
    transform: none;
    font-size: 0.64rem;
  }

  .exchange-offers-marker--origin .exchange-offers-marker__pin {
    background: ${({ theme }) => theme.colors.apple.hex()};
  }

  .exchange-offers-marker--destination .exchange-offers-marker__pin {
    background: ${({ theme }) => theme.colors.easternBlue.hex()};
  }

  .exchange-offers-marker--return .exchange-offers-marker__pin {
    background: ${ROUND_TRIP_ROUTE_COLOR};
  }

  .exchange-offers-marker--round-trip-destination .exchange-offers-marker__pin {
    background: #ef8b2c;
  }

  .exchange-offers-marker--round-trip-export .exchange-offers-marker__pin {
    background: ${ROUND_TRIP_EXPORT_COLOR};
  }

  .exchange-offers-marker--round-trip-general .exchange-offers-marker__pin {
    background: #ef8b2c;
  }

  .exchange-offers-marker__count {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: auto;
    padding: 0;
    transform: rotate(45deg);
    border: 0;
    background: transparent;
    color: white;
    font-size: 0.54rem;
    font-weight: 800;
    line-height: 1;
    transition: filter 0.15s ease, opacity 0.15s ease;
  }

  .exchange-offers-marker__favorite {
    position: absolute;
    top: 1px;
    right: 0;
    z-index: 2;
    color: #f4b400;
    filter: none;
    font-size: 0.78rem;
    line-height: 1;
    paint-order: stroke fill;
    pointer-events: none;
    text-shadow: 0 1px 2px rgba(36, 45, 63, 0.34);
    -webkit-text-stroke: 2px white;
  }

  .exchange-offers-marker--cluster .exchange-offers-marker__favorite {
    top: 1px;
    right: 0;
    font-size: 0.85rem;
  }

  .exchange-offers-marker--dimmed .exchange-offers-marker__pin {
    filter: grayscale(0.8);
    opacity: 0.34;
  }

  .exchange-offers-marker--dimmed .exchange-offers-marker__favorite {
    color: ${({ theme }) => theme.colors.corduroy.hex()};
    opacity: 0.42;
  }

  .exchange-offers-marker__tooltip {
    position: absolute;
    bottom: calc(100% + 4px);
    left: 50%;
    z-index: 3;
    width: max-content;
    max-width: 420px;
    padding: 5px 8px;
    transform: translate(-50%, 3px);
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.ebonyClay.alpha(0.96).hex()};
    box-shadow: 0 2px 8px rgba(36, 45, 63, 0.22);
    color: white;
    font-size: 0.67rem;
    font-weight: 600;
    line-height: 1.25;
    opacity: 0;
    pointer-events: none;
    text-align: center;
    transition: opacity 0.12s ease, transform 0.12s ease;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .exchange-offers-marker:hover .exchange-offers-marker__tooltip,
  .exchange-offers-marker:focus-visible .exchange-offers-marker__tooltip {
    transform: translate(-50%, 0);
    opacity: 1;
  }

  .exchange-offers-map--tooltips-suppressed .exchange-offers-marker__tooltip {
    opacity: 0 !important;
    transition: none;
  }

  ${breakpoint.m`
    height: ${({ compact }) => (compact ? '460px' : 'calc(100vh - 205px)')};
    min-height: ${({ compact }) => (compact ? '460px' : '560px')};
  `}
`
